import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { usePermission } from '../hooks/usePermission';
import { useAuth } from '../hooks/useAuth';
import { AccessLevel } from '../types';
import { 
  ArrowLeft, Save, Trash2, User, ShieldCheck, 
  Camera, Upload, X, ChevronDown, ChevronUp, FileText, Lock,
  Plus, MapPin, Map as MapIcon, Edit, Navigation, AlertTriangle, ExternalLink,
  Mail, Facebook, Instagram, Linkedin, Youtube, Twitch, Send, MessageCircle, MessageSquare, Globe, Link, Sparkles,
  Users, CheckCircle, Check, Phone, Award, Calendar, Hash, Compass, Code
} from 'lucide-react';
import { APIProvider, Map as GMap, AdvancedMarker, Pin, InfoWindow, useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { toast } from 'sonner';
import { SignedImage } from '../components/SignedImage';
import { getPersonPhotoPath, cn, formatName } from '../lib/utils';
import { validaCodiceFiscale, verificaCongruenzaCF } from '../lib/dbHelper';
import { motion, AnimatePresence } from 'motion/react';
import { cache } from '../lib/cache';
import { ComboBox } from '../components/ComboBox';
import { SezioniSocio, DEFAULT_SEZIONI_TEMPLATES, serializeAttributiSezioni } from '../components/SezioniSocio';

const DettaglioPersona: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission, hasGroup } = usePermission();
  const { user } = useAuth();
  
  const action = searchParams.get('action') || 'view';
  const isEditing = action === 'edit' || action === 'add';
  const isNew = action === 'add' || !id || id === 'new';
  const isOwnCard = user?.personaId !== undefined && Number(id) === Number(user.personaId);

  const canWrite = hasPermission('app.segreteria.anagrafica.persone', AccessLevel.rw) || isOwnCard;
  const canRead = hasPermission('app.segreteria.anagrafica', AccessLevel.ro) || isOwnCard;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [isSectionOpen, setIsSectionOpen] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<any | null>(null);
  const [emailToDelete, setEmailToDelete] = useState<any | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const cancelBtnRef = React.useRef<HTMLButtonElement>(null);
  
  const [data, setData] = useState<any>({
    cognome: '',
    nome: '',
    codice_fiscale: '',
    partita_iva: '',
    sesso: 'M',
    data_nascita: '',
    tipologia_anagrafica: 1,
    titolo_onorifico: null,
    id_nazione_nascita: 380,
    id_regione_nascita: null,
    id_provincia_nascita: null,
    id_comune_nascita: null,
    id_anpr: '',
    titolo_studio: null,
    note: ''
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [useCamera, setUseCamera] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const [titoli, setTitoli] = useState<any[]>([]);
  const [nazioni, setNazioni] = useState<any[]>([]);
  const [comuni, setComuni] = useState<any[]>([]);
  const [comuneSearch, setComuneSearch] = useState('');
  const [isSearchingComune, setIsSearchingComune] = useState(false);
  const [belfioreCode, setBelfioreCode] = useState<string | null>(null);
  const [birthPlaceLabels, setBirthPlaceLabels] = useState({ regione: '', provincia: '' });
  const [tipologieAnagrafiche, setTipologieAnagrafiche] = useState<any[]>([]);
  const [titoliStudio, setTitoliStudio] = useState<any[]>([]);

  // States per "Indirizzi" Accordion ed elaborazione
  const [indirizzi, setIndirizzi] = useState<any[]>([]);
  const [loadingIndirizzi, setLoadingIndirizzi] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [tipologieIndirizzi, setTipologieIndirizzi] = useState<any[]>([]);

  // States per "Emails" Accordion ed elaborazione
  const [emails, setEmails] = useState<any[]>([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [isAccordionEmailsOpen, setIsAccordionEmailsOpen] = useState(false);
  const [tipologieEmails, setTipologieEmails] = useState<any[]>([]);
  const [isAddingEmail, setIsAddingEmail] = useState(false);
  const [editingEmailId, setEditingEmailId] = useState<number | string | null>(null);
  const [deletingEmailId, setDeletingEmailId] = useState<number | string | null>(null);
  const [emailForm, setEmailForm] = useState<any>({
    id_tipo_mail: '',
    email: '',
    is_pec: false
  });

  // States per "Social Networks" Accordion ed elaborazione
  const [socials, setSocials] = useState<any[]>([]);
  const [loadingSocials, setLoadingSocials] = useState(false);
  const [isAccordionSocialsOpen, setIsAccordionSocialsOpen] = useState(false);
  const [tipologieSocial, setTipologieSocial] = useState<any[]>([]);
  const [isAddingSocial, setIsAddingSocial] = useState(false);
  const [editingSocialId, setEditingSocialId] = useState<number | string | null>(null);
  const [socialToDelete, setSocialToDelete] = useState<any | null>(null);
  const [socialForm, setSocialForm] = useState<any>({
    id_social: '',
    username1: '',
    username2: ''
  });

  // States per verifica OTP (codice casuale di 5 cifre)
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpTokenId, setOtpTokenId] = useState('');
  const [otpCodeInput, setOtpCodeInput] = useState('');
  const [otpError, setOtpError] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpEmailTarget, setOtpEmailTarget] = useState('');
  const [otpPendingAction, setOtpPendingAction] = useState<'SAVE' | 'DELETE' | null>(null);
  
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [addressForm, setAddressForm] = useState<any>({
    id_tipologia_indirizzo: '',
    id_comune: '',
    cap: '',
    odonimo: '',
    civico: '',
    esponente: '',
    progressivo_nazionale: '',
    latitudine: '',
    longitudine: ''
  });

  const [addrComuniOptions, setAddrComuniOptions] = useState<any[]>([]);
  const [addrComuneSearch, setAddrComuneSearch] = useState('');
  const [isSearchingAddrComune, setIsSearchingAddrComune] = useState(false);

  const [odonimiOptions, setOdonimiOptions] = useState<any[]>([]);
  const [odonimoSearch, setOdonimoSearch] = useState('');
  const [isSearchingOdonimo, setIsSearchingOdonimo] = useState(false);

  const [selectedMapAddress, setSelectedMapAddress] = useState<any | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [mapsApiKey, setMapsApiKey] = useState<string>('');
  const [proposedCoords, setProposedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isUpdatingCoords, setIsUpdatingCoords] = useState(false);

  // --- TELEFONI STATES ---
  const [telefoni, setTelefoni] = useState<any[]>([]);
  const [loadingTelefoni, setLoadingTelefoni] = useState(false);
  const [isAccordionTelefoniOpen, setIsAccordionTelefoniOpen] = useState(false);
  const [tipologieTelefoni, setTipologieTelefoni] = useState<any[]>([]);
  const [isAddingTelefono, setIsAddingTelefono] = useState(false);
  const [editingTelefonoId, setEditingTelefonoId] = useState<number | string | null>(null);
  const [telefonoToDelete, setTelefonoToDelete] = useState<any | null>(null);
  const [telefonoForm, setTelefonoForm] = useState<any>({
    id_tipoologia_telefono: '',
    numero: '',
    note: ''
  });

  // --- SCHEDA SOCIO (CPGEV_SOCI) STATES ---
  const [socioData, setSocioData] = useState<any | null>(null);
  const [loadingSocio, setLoadingSocio] = useState(false);
  const [savingSocio, setSavingSocio] = useState(false);
  const [isAccordionSocioOpen, setIsAccordionSocioOpen] = useState(true);
  const [isEditingSocio, setIsEditingSocio] = useState(false);
  const [isAddingSocio, setIsAddingSocio] = useState(false);
  const [showDeleteSocioModal, setShowDeleteSocioModal] = useState(false);
  // Default stati socio da tabella cpgev_stato_socio
  const DEFAULT_STATI_SOCIO: Array<{ tipo: number; descrizione: string }> = [
    { tipo: 1, descrizione: 'Socio' },
    { tipo: 2, descrizione: 'Aspiranti GEV' },
    { tipo: 3, descrizione: 'Cessati' },
    { tipo: 4, descrizione: 'Corso regione' },
    { tipo: 5, descrizione: 'Domanda di ammissione' },
    { tipo: 6, descrizione: 'GEV in servizio' },
    { tipo: 7, descrizione: 'Giuramento' },
    { tipo: 8, descrizione: 'Guardia Parco' },
    { tipo: 9, descrizione: 'Idonei' },
    { tipo: 10, descrizione: 'In attesa prefettura' },
    { tipo: 11, descrizione: 'Nominati G.P.G' },
    { tipo: 12, descrizione: 'Rinnovo G.P.G.' },
    { tipo: 13, descrizione: 'Rinnovo GEV' }
  ];

  const [statiSocioList, setStatiSocioList] = useState<Array<{ tipo: number; descrizione: string }>>(() => {
    const cached = cache.get<any[]>('lookup_stati_socio');
    if (cached && Array.isArray(cached) && cached.length > 0) return cached;
    return DEFAULT_STATI_SOCIO;
  });
  const [zoneList, setZoneList] = useState<Array<{ id: number; descrizione: string }>>([]);
  const [socioForm, setSocioForm] = useState<any>({
    stato_socio: 1,
    zona: 8,
    matricola: '',
    data_iscrizione: new Date().toISOString().split('T')[0],
    data_cessazione: '',
    attributi_aggiuntivi: {}
  });
  const [jsonAttributiRaw, setJsonAttributiRaw] = useState('{}');
  const [jsonAttributiError, setJsonAttributiError] = useState('');

  // --- STATO DI FAMIGLIA STATES ---
  const [parentele, setParentele] = useState<any[]>([]);
  const [famiglia, setFamiglia] = useState<any | null>(null);
  const [famigliaMembri, setFamigliaMembri] = useState<any[]>([]);
  const [loadingFamiglia, setLoadingFamiglia] = useState(false);
  const [isEditingFamiglia, setIsEditingFamiglia] = useState(false);
  const [isAccordionFamigliaOpen, setIsAccordionFamigliaOpen] = useState(false);
  const [famigliaDenominazione, setFamigliaDenominazione] = useState('');
  const [editingMembri, setEditingMembri] = useState<Array<{ id_anagrafica: number; id_parentela: number }>>([]);
  const [personeList, setPersoneList] = useState<any[]>([]);

  // --- PDF PARSING AND VIRTUAL PERSON STATES ---
  const [parsingPDF, setParsingPDF] = useState(false);
  const [parsedPDFData, setParsedPDFData] = useState<any | null>(null);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const [isCreaNuovaPersonaOpen, setIsCreaNuovaPersonaOpen] = useState(false);
  const [curMemberIndexForNew, setCurMemberIndexForNew] = useState<number | null>(null);
  const [nuovaPersonaForm, setNuovaPersonaForm] = useState({
    cognome: '',
    nome: '',
    codice_fiscale: '',
    sesso: 'M',
    data_nascita: '',
    comune_nascita: '',
    provincia_nascita: ''
  });
  const [isSavingVirtualPersona, setIsSavingVirtualPersona] = useState(false);

  useEffect(() => {
    fetch('/api/config/maps-key')
      .then(res => res.json())
      .then(data => {
        if (data && data.key) {
          setMapsApiKey(data.key);
        }
      })
      .catch(err => console.error('Errore nel recupero della chiave Google Maps:', err));
  }, []);

  useEffect(() => {
    setProposedCoords(null);
  }, [selectedMapAddress]);

  // Caricamento coordinate da cpgev_indirizzario_italia se progressivo_nazionale viene inserito o cambia
  useEffect(() => {
    if (!addressForm.progressivo_nazionale) return;
    const fetchCoords = async () => {
      try {
        const { data } = await supabase
          .from('cpgev_indirizzario_italia')
          .select('latitudine, longitudine')
          .eq('progressivo_nazionale', Number(addressForm.progressivo_nazionale))
          .maybeSingle();
        if (data?.latitudine && data?.longitudine) {
          setAddressForm(prev => ({
            ...prev,
            latitudine: prev.latitudine ? prev.latitudine : String(data.latitudine),
            longitudine: prev.longitudine ? prev.longitudine : String(data.longitudine)
          }));
        }
      } catch (err) {
        console.error('Errore nel ricavare coordinate da indirizzario:', err);
      }
    };
    fetchCoords();
  }, [addressForm.progressivo_nazionale]);

  useEffect(() => {
    if (!isNew && id) {
      fetchPersona();
      fetchFamiglia();
    }
  }, [id, isNew]);

  useEffect(() => {
    const fetchLookups = async () => {
      // 1. Titoli
      const titoliCache = cache.get<any[]>('lookup_titoli');
      if (titoliCache) setTitoli(titoliCache);
      else {
        const { data } = await supabase.from('cpgev_titoli_onorifici').select('id, abbreviazione').order('abbreviazione');
        if (data) { 
          const opts = data.map(t => ({ id: t.id, label: t.abbreviazione }));
          setTitoli(opts); 
          cache.set('lookup_titoli', opts); 
        }
      }

      // 2. Nazioni
      const nazioniCache = cache.get<any[]>('lookup_nazioni');
      if (nazioniCache) setNazioni(nazioniCache);
      else {
        const { data } = await supabase.from('cpgev_nazioni').select('id, denominazione, immagine').order('denominazione');
        if (data) { 
          const opts = data.map(n => ({ id: n.id, label: n.denominazione, image: n.immagine }));
          setNazioni(opts); 
          cache.set('lookup_nazioni', opts); 
        }
      }

      // 3. Tipologie Anagrafiche
      const tipologieCache = cache.get<any[]>('lookup_tipologie_anagrafica');
      if (tipologieCache) setTipologieAnagrafiche(tipologieCache);
      else {
        const { data } = await supabase.from('cpgev_tipologia_anagrafiche').select('id, descrizione').order('id');
        if (data) {
          setTipologieAnagrafiche(data);
          cache.set('lookup_tipologie_anagrafica', data);
        }
      }

      // 4. Titoli Studio
      const studioCache = cache.get<any[]>('lookup_titoli_studio');
      if (studioCache) setTitoliStudio(studioCache);
      else {
        const { data } = await supabase.from('cpgev_titoli_studio').select('id, titolo_studio').order('titolo_studio');
        if (data) {
          const opts = data.map(s => ({ id: s.id, label: s.titolo_studio }));
          setTitoliStudio(opts);
          cache.set('lookup_titoli_studio', opts);
        }
      }

      // 5. Tipologie Indirizzi
      const { data: tipIndData } = await supabase.from('cpgev_tipologie_indirizzi').select('id, descrizione').order('id');
      if (tipIndData) {
        setTipologieIndirizzi(tipIndData);
      }

      // 6. Tipologie Emails
      try {
        const { data: tipEmailData } = await supabase.from('cpgev_tipi_emails').select('id, tipo_email').order('id');
        if (tipEmailData && tipEmailData.length > 0) {
          setTipologieEmails(tipEmailData);
        } else {
          setTipologieEmails([
            { id: 1, tipo_email: '1 - Personale' },
            { id: 2, tipo_email: '2 - Di Lavoro' },
            { id: 3, tipo_email: '3 - Altro' },
            { id: 99, tipo_email: '99 - Associazione' }
          ]);
        }
      } catch (err) {
        console.error('Errore caricamento tipi email:', err);
        setTipologieEmails([
          { id: 1, tipo_email: '1 - Personale' },
          { id: 2, tipo_email: '2 - Di Lavoro' },
          { id: 3, tipo_email: '3 - Altro' },
          { id: 99, tipo_email: '99 - Associazione' }
        ]);
      }

      // 7. Tipologie Social
      try {
        const { data: tipSocialData } = await supabase.from('cpgev_tipologie_social').select('*').order('id');
        if (tipSocialData && tipSocialData.length > 0) {
          setTipologieSocial(tipSocialData);
        } else {
          setTipologieSocial([
            { id: 1, piattaforma: 'BeReal', link_base: 'https://bere.al/' },
            { id: 2, piattaforma: 'Discord', link_base: 'https://discord.com/users/' },
            { id: 3, piattaforma: 'Facebook', link_base: 'https://facebook.com/' },
            { id: 4, piattaforma: 'Instagram', link_base: 'https://instagram.com/' },
            { id: 5, piattaforma: 'LinkedIn', link_base: 'https://linkedin.com/in/' },
            { id: 6, piattaforma: 'Mastodon', link_base: 'https://mastodon.social/@' },
            { id: 7, piattaforma: 'Messenger', link_base: 'https://m.me/' },
            { id: 8, piattaforma: 'Pinterest', link_base: 'https://pinterest.com/' },
            { id: 9, piattaforma: 'Reddit', link_base: 'https://reddit.com/user/' },
            { id: 10, piattaforma: 'Snapchat', link_base: 'https://snapchat.com/add/' },
            { id: 11, piattaforma: 'Telegram', link_base: 'https://t.me/' },
            { id: 12, piattaforma: 'Threads', link_base: 'https://threads.net/@' },
            { id: 13, piattaforma: 'TikTok', link_base: 'https://tiktok.com/@' },
            { id: 14, piattaforma: 'Tumblr', link_base: 'https://$username.tumblr.com' },
            { id: 15, piattaforma: 'Twitch', link_base: 'https://twitch.tv/' },
            { id: 16, piattaforma: 'WhatsApp', link_base: 'https://wa.me/' },
            { id: 17, piattaforma: 'X (Twitter)', link_base: 'https://x.com/' },
            { id: 18, piattaforma: 'YouTube', link_base: 'https://youtube.com/@' }
          ]);
        }
      } catch (err) {
        console.error('Errore caricamento tipi social:', err);
        setTipologieSocial([
          { id: 1, piattaforma: 'BeReal', link_base: 'https://bere.al/' },
          { id: 2, piattaforma: 'Discord', link_base: 'https://discord.com/users/' },
          { id: 3, piattaforma: 'Facebook', link_base: 'https://facebook.com/' },
          { id: 4, piattaforma: 'Instagram', link_base: 'https://instagram.com/' },
          { id: 5, piattaforma: 'LinkedIn', link_base: 'https://linkedin.com/in/' },
          { id: 6, piattaforma: 'Mastodon', link_base: 'https://mastodon.social/@' },
          { id: 7, piattaforma: 'Messenger', link_base: 'https://m.me/' },
          { id: 8, piattaforma: 'Pinterest', link_base: 'https://pinterest.com/' },
          { id: 9, piattaforma: 'Reddit', link_base: 'https://reddit.com/user/' },
          { id: 10, piattaforma: 'Snapchat', link_base: 'https://snapchat.com/add/' },
          { id: 11, piattaforma: 'Telegram', link_base: 'https://t.me/' },
          { id: 12, piattaforma: 'Threads', link_base: 'https://threads.net/@' },
          { id: 13, piattaforma: 'TikTok', link_base: 'https://tiktok.com/@' },
          { id: 14, piattaforma: 'Tumblr', link_base: 'https://$username.tumblr.com' },
          { id: 15, piattaforma: 'Twitch', link_base: 'https://twitch.tv/' },
          { id: 16, piattaforma: 'WhatsApp', link_base: 'https://wa.me/' },
          { id: 17, piattaforma: 'X (Twitter)', link_base: 'https://x.com/' },
          { id: 18, piattaforma: 'YouTube', link_base: 'https://youtube.com/@' }
        ]);
      }

      // 8. Parentele (for families)
      try {
        const { data: parenteleData } = await supabase.from('cpgev_parentele').select('*').order('id', { ascending: true });
        if (parenteleData) {
          setParentele(parenteleData);
        }
      } catch (err) {
        console.error('Errore caricamento parentele:', err);
      }

      // 9. Tipologie Telefoni
      try {
        const { data: tipTelData, error: tipTelError } = await supabase
          .from('cpgev_tipologia_telefoni')
          .select('id, tipologia_telefono')
          .order('id', { ascending: true });
        
        if (tipTelError) throw tipTelError;
        
        if (tipTelData) {
          setTipologieTelefoni(tipTelData);
        }
      } catch (err: any) {
        console.error('Errore caricamento tipi telefono da Postgresql:', err);
        toast.error('Errore nel caricamento delle tipologie dei telefoni da database: ' + err.message);
      }

      // 10. Stati Socio (cpgev_stato_socio)
      try {
        const statiCache = cache.get<any[]>('lookup_stati_socio');
        if (statiCache && Array.isArray(statiCache) && statiCache.length > 0) {
          setStatiSocioList(statiCache);
        } else {
          let list: any[] = [];
          const { data: statiData, error: statiErr } = await supabase
            .from('cpgev_stato_socio')
            .select('tipo, descrizione')
            .order('tipo', { ascending: true });
          
          if (!statiErr && statiData && statiData.length > 0) {
            list = statiData;
          } else {
            // Fallback per bypassare RLS tramite API server
            try {
              const res = await fetch('/api/stati-socio');
              if (res.ok) {
                const apiData = await res.json();
                if (Array.isArray(apiData) && apiData.length > 0) {
                  list = apiData;
                }
              }
            } catch (apiErr) {
              console.warn('Fallback /api/stati-socio non disponibile:', apiErr);
            }
          }

          if (list.length > 0) {
            setStatiSocioList(list);
            cache.set('lookup_stati_socio', list);
          } else {
            setStatiSocioList(DEFAULT_STATI_SOCIO);
          }
        }
      } catch (err: any) {
        console.error('Errore caricamento stati socio da Postgresql:', err);
        setStatiSocioList(DEFAULT_STATI_SOCIO);
      }

      // 11. Zone (cpgev_codici_zone)
      try {
        const zoneCache = cache.get<any[]>('lookup_zone_socio');
        if (zoneCache) {
          setZoneList(zoneCache);
        } else {
          const { data: zoneData, error: zoneErr } = await supabase
            .from('cpgev_codici_zone')
            .select('id, descrizione')
            .order('id', { ascending: true });
          if (zoneErr) throw zoneErr;
          if (zoneData) {
            setZoneList(zoneData);
            cache.set('lookup_zone_socio', zoneData);
          }
        }
      } catch (err: any) {
        console.error('Errore caricamento zone da Postgresql:', err);
      }
    };
    fetchLookups();
  }, []);

  // Decodifica automatica in base agli ID selezionati o presenti nel record
  const [decodedLabels, setDecodedLabels] = useState({
    nazione: '',
    nazioneImmagine: '',
    comune: '',
    comuneImmagine: '',
    provincia: '',
    provinciaImmagine: '',
    regione: '',
    regioneImmagine: ''
  });

  useEffect(() => {
    const decodeAllBirthPlace = async () => {
      let nCol = '';
      let nImg = '';
      let cCol = '';
      let cImg = '';
      let pCol = '';
      let pImg = '';
      let rCol = '';
      let rImg = '';

      // 1. Decodifica Nazione
      if (data.id_nazione_nascita) {
        const found = nazioni.find(n => n.id === data.id_nazione_nascita);
        if (found) {
          nCol = found.label;
          nImg = found.image || '';
        } else {
          try {
            const { data: n } = await supabase.from('cpgev_nazioni').select('denominazione, immagine').eq('id', data.id_nazione_nascita).single();
            if (n) {
              nCol = n.denominazione;
              nImg = n.immagine || '';
            }
          } catch (e) {}
        }
      }

      // 2. Decodifica Comune
      if (data.id_comune_nascita) {
        try {
          const { data: c } = await supabase.from('cpgev_comuni').select('id, denominazione, immagine, id_provincia, id_regione').eq('id', data.id_comune_nascita).single();
          if (c) {
            cCol = c.denominazione;
            cImg = c.immagine || '';
            // Impostiamo belfioreCode all'id del comune (che è il codice Belfiore, es 'A944')
            setBelfioreCode(c.id);
            
            // Decodifica anche provincia e regione se presenti e non impostate nell'anagrafica
            if (!data.id_provincia_nascita && c.id_provincia) {
              setData((prev: any) => ({ ...prev, id_provincia_nascita: c.id_provincia }));
            }
            if (!data.id_regione_nascita && c.id_regione) {
              setData((prev: any) => ({ ...prev, id_regione_nascita: c.id_regione }));
            }
          }
        } catch (e) {}
      } else {
        setBelfioreCode(null);
      }

      // 3. Decodifica Provincia
      if (data.id_provincia_nascita) {
        try {
          const { data: p } = await supabase.from('cpgev_provincie').select('denominazione, immagine').eq('id', data.id_provincia_nascita).single();
          if (p) {
            pCol = p.denominazione;
            pImg = p.immagine || '';
          }
        } catch (e) {}
      }

      // 4. Decodifica Regione
      if (data.id_regione_nascita) {
        try {
          const { data: r } = await supabase.from('cpgev_regioni').select('denominazione, immagine').eq('id', data.id_regione_nascita).single();
          if (r) {
            rCol = r.denominazione;
            rImg = r.immagine || '';
          }
        } catch (e) {}
      }

      setDecodedLabels({
        nazione: nCol,
        nazioneImmagine: nImg,
        comune: cCol,
        comuneImmagine: cImg,
        provincia: pCol,
        provinciaImmagine: pImg,
        regione: rCol,
        regioneImmagine: rImg
      });

      // Manteniamo sincronizzato il vecchio state per sicurezza
      setBirthPlaceLabels({
        regione: rCol,
        provincia: pCol
      });
    };

    decodeAllBirthPlace();
  }, [data.id_nazione_nascita, data.id_comune_nascita, data.id_provincia_nascita, data.id_regione_nascita, nazioni]);

  // Autocompletamento Comuni: "dopo aver selezionato la nazione e digitato almeno 2 caratteri nel nome del comune" & "iniziano con le lettere digitate (ricerca tutto in minuscolo)"
  useEffect(() => {
    if (!data.id_nazione_nascita || comuneSearch.length < 2) { 
      setComuni([]); 
      setIsSearchingComune(false); 
      return; 
    }
    
    const fetchComuni = async () => {
      setIsSearchingComune(true);
      try {
        const { data: res, error } = await supabase
          .from('cpgev_comuni')
          .select('id, denominazione, immagine, id_provincia, id_regione')
          .eq('id_nazione', data.id_nazione_nascita)
          .ilike('denominazione', `${comuneSearch}%`) // Ricerca per prefisso / iniziano con...
          .limit(10); // Limitato a 10 per performance ed eleganza della lista

        if (error) {
          console.error('Errore query comuni:', error);
          toast.error(`Errore caricamento comuni: ${error.message}`);
          setIsSearchingComune(false);
          return;
        }

        if (res) { 
          const opts = res.map((c: any) => ({ 
            id: c.id, 
            label: c.denominazione, 
            image: c.immagine,
            belfiore: c.id, // Belfiore code is key id!
            provId: c.id_provincia,
            regId: c.id_regione
          }));
          setComuni(opts); 
        }
      } catch (err) {
        console.error('Eccezione durante fetchComuni:', err);
      } finally {
        setIsSearchingComune(false);
      }
    };

    const timer = setTimeout(fetchComuni, 300);
    return () => {
      clearTimeout(timer);
      setIsSearchingComune(false);
    };
  }, [data.id_nazione_nascita, comuneSearch]);

  const handleComuneChange = (val: any) => {
    // Tenta di trovare il comune nei comuni autocompletati o nel comune fittizio prepopolato
    const matchOptions = [...comuni];
    if (data.id_comune_nascita && decodedLabels.comune) {
      matchOptions.push({
        id: data.id_comune_nascita,
        label: decodedLabels.comune,
        belfiore: data.id_comune_nascita,
        provId: data.id_provincia_nascita,
        regId: data.id_regione_nascita
      });
    }

    const selectedComune = matchOptions.find(c => c.id === val);
    if (selectedComune) {
      setData((prev: any) => ({
        ...prev, 
        id_comune_nascita: selectedComune.id,
        id_provincia_nascita: selectedComune.provId,
        id_regione_nascita: selectedComune.regId
      }));
      setBelfioreCode(selectedComune.belfiore);
    } else {
      setData((prev: any) => ({
        ...prev,
        id_comune_nascita: val,
        id_provincia_nascita: null,
        id_regione_nascita: null
      }));
      setBelfioreCode(null);
    }
  };

  const getDayOfWeek = (dateStr: string) => {
    if (!dateStr) return '';
    const days = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return days[date.getDay()];
  };

  const checkGenderCongruence = (nome: string, sesso: string) => {
    if (!nome || !sesso || sesso === 'A') return;
    const n = nome.trim().toLowerCase();
    // Euristiche semplici per l'italiano
    const endsInA = n.endsWith('a');
    const endsInO = n.endsWith('o');
    
    if (endsInA && sesso === 'M') {
      toast.warning(`Il nome ${nome} sembra femminile ma il sesso è Maschio`, { duration: 4000 });
    } else if (endsInO && sesso === 'F') {
      toast.warning(`Il nome ${nome} sembra maschile ma il sesso è Femmina`, { duration: 4000 });
    }
  };

  const validateCFOnBlur = () => {
    if (!data.codice_fiscale) return;
    
    if (!validaCodiceFiscale(data.codice_fiscale)) {
      toast.error('Codice Fiscale non valido formalmente o nel carattere di controllo');
    } else if (data.data_nascita && data.sesso && data.cognome && data.nome) {
      const resp = verificaCongruenzaCF(
        data.codice_fiscale, 
        data.data_nascita, 
        data.sesso, 
        data.cognome, 
        data.nome, 
        belfioreCode || undefined
      );
      if (!resp.valid) {
        toast.error(resp.message, { duration: 6000 });
      } else {
        toast.success('Codice Fiscale congruente con i dati inseriti');
      }
    }
  };

  useEffect(() => {
    if (isEditing && cancelBtnRef.current) {
      setTimeout(() => cancelBtnRef.current?.focus(), 100);
    }
  }, [isEditing]);

  const fetchPersona = async () => {
    try {
      setLoading(true);
      const { data: persona, error } = await supabase
        .from('cpgev_anagrafica')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (persona) setData(persona);
    } catch (error: any) {
      toast.error('Errore nel caricamento della persona');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFamiglia = async () => {
    if (!id || id === 'new') return;
    try {
      setLoadingFamiglia(true);
      const { data: nucData, error: nucError } = await supabase
        .from('cpgev_nuclei_familiari')
        .select('id_famiglia, id_parentela')
        .eq('id_anagrafica', Number(id))
        .is('data_fine_validita', null);

      if (nucError) throw nucError;

      if (nucData && nucData.length > 0) {
        const familyId = nucData[0].id_famiglia;

        const { data: famData, error: famError } = await supabase
          .from('cpgev_famiglie')
          .select('id_famiglia, denominazione, data_inserimento')
          .eq('id_famiglia', familyId)
          .maybeSingle();

        if (famError) throw famError;
        setFamiglia(famData);

        const { data: members, error: membersError } = await supabase
          .from('cpgev_nuclei_familiari')
          .select(`
            id,
            id_famiglia,
            id_anagrafica,
            id_parentela,
            data_inizio_validita,
            data_fine_validita,
            cpgev_anagrafica (
              id,
              cognome,
              nome,
              codice_fiscale
            ),
            cpgev_parentele (
              id,
              descrizione
            )
          `)
          .eq('id_famiglia', familyId);

        if (membersError) throw membersError;
        setFamigliaMembri(members || []);
      } else {
        setFamiglia(null);
        setFamigliaMembri([]);
      }
    } catch (err: any) {
      console.error('Errore nel caricamento della famiglia:', err);
    } finally {
      setLoadingFamiglia(false);
    }
  };

  const handlePDFUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setParsingPDF(true);
      toast.info("Caricamento ed analisi del PDF in corso con l'AI...");

      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = (reader.result as string).split(',')[1];
          const addressText = indirizzi && indirizzi.length > 0
            ? `${indirizzi[0].via || ''} ${indirizzi[0].civico || ''}, ${indirizzi[0].cap || ''} ${indirizzi[0].comuneDenom || ''} (${indirizzi[0].provDenom || ''})`
            : '';

          const response = await fetch('/api/sys/parse-family-pdf', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pdfBase64: base64String,
              currentPersona: {
                cognome: data.cognome,
                nome: data.nome,
                codice_fiscale: data.codice_fiscale,
                indirizzo_residenza: addressText
              }
            })
          });

          if (!response.ok) {
            const errData = await response.json();
            throw new Error(errData.error || "Errore del server durante l'analisi");
          }

          const parsedData = await response.json();
          setParsedPDFData(parsedData);
          toast.success("Documento analizzato! Clicca sulla bacchetta magica ✨ per precompilare.");
        } catch (err: any) {
          console.error(err);
          toast.error(`Errore analisi PDF: ${err.message}`);
        } finally {
          setParsingPDF(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err: any) {
      console.error(err);
      toast.error(`Errore caricamento del file.`);
      setParsingPDF(false);
    }
  };

  const handleApplyMagicProposal = async () => {
    if (!parsedPDFData) {
      toast.error("Nessun dato PDF caricato o analizzato.");
      return;
    }

    // 1. Forza il caricamento della lista persone se non già caricata
    let currentPersone = personeList;
    if (personeList.length === 0) {
      try {
        const { data: listData } = await supabase
          .from('cpgev_anagrafica')
          .select('id, cognome, nome, codice_fiscale')
          .order('cognome', { ascending: true });
        if (listData) {
          currentPersone = listData;
          setPersoneList(listData);
        }
      } catch (e) {
        console.error('Errore caricamento persone:', e);
      }
    }

    // 2. Imposta dinamicamente i dati precompilati
    if (parsedPDFData.estremi_atto) {
      setFamigliaDenominazione(parsedPDFData.estremi_atto);
    }

    // 3. Elabora i componenti estratti
    const mappedMembri = parsedPDFData.componenti.map((comp: any) => {
      // Trova compatibilità con persone esistenti nel DB
      let matchedId = 0;
      const match = currentPersone.find((p: any) => 
        p.cognome?.trim().toUpperCase() === comp.cognome?.trim().toUpperCase() &&
        p.nome?.trim().toUpperCase() === comp.nome?.trim().toUpperCase()
      );
      if (match) {
        matchedId = Number(match.id);
      }

      // Trova compatibilità parentela
      let parentelaId = 0;
      const ptMatch = parentele.find((p: any) => 
        p.descrizione?.trim().toLowerCase() === comp.rapporto_parentela?.trim().toLowerCase()
      );
      if (ptMatch) {
         parentelaId = Number(ptMatch.id);
      } else {
        // Fallback su combinazioni comuni
        const parentL = comp.rapporto_parentela?.toLowerCase() || '';
        if (parentL.includes('figl')) {
          const fMatch = parentele.find(p => p.descrizione.toLowerCase().includes('figlio') || p.descrizione.toLowerCase().includes('figlia'));
          if (fMatch) parentelaId = Number(fMatch.id);
        } else if (parentL.includes('coniuge') || parentL.includes('moglie') || parentL.includes('marito')) {
          const cMatch = parentele.find(p => p.descrizione.toLowerCase().includes('coniuge') || p.descrizione.toLowerCase().includes('moglie'));
          if (cMatch) parentelaId = Number(cMatch.id);
        }
      }

      return {
        id_anagrafica: matchedId,
        id_parentela: parentelaId,
        pdf_info: comp // Memorizziamo le info
      };
    });

    setEditingMembri(mappedMembri);
    setIsEditingFamiglia(true);
    toast.success("Bacchetta magica applicata con successo! I campi sono stati precompilati.");
  };

  const handleSaveVirtualPersona = async () => {
    if (!nuovaPersonaForm.cognome.trim() || !nuovaPersonaForm.nome.trim()) {
      toast.error("Cognome e Nome sono obbligatori.");
      return;
    }

    try {
      setIsSavingVirtualPersona(true);

      // Normalizzazione data nascita
      let formattedDate: string | null = null;
      if (nuovaPersonaForm.data_nascita) {
        if (/^\d{4}-\d{2}-\d{2}$/.test(nuovaPersonaForm.data_nascita)) {
          formattedDate = nuovaPersonaForm.data_nascita;
        } else {
          const m = nuovaPersonaForm.data_nascita.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
          if (m) {
            formattedDate = `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
          }
        }
      }

      // Inserisce su cpgev_anagrafica
      const insertPayload = {
        cognome: nuovaPersonaForm.cognome.trim(),
        nome: nuovaPersonaForm.nome.trim(),
        codice_fiscale: nuovaPersonaForm.codice_fiscale.trim().toUpperCase() || null,
        sesso: nuovaPersonaForm.sesso || 'M',
        data_nascita: formattedDate,
        tipologia_anagrafica: 1, // Persona Fisica / Standard
        note: "Registrato virtualmente tramite importazione del PDF d'anagrafica."
      };

      const { data: insData, error: insError } = await supabase
        .from('cpgev_anagrafica')
        .insert([insertPayload])
        .select('id, cognome, nome, codice_fiscale')
        .single();

      if (insError) throw insError;

      if (insData) {
        toast.success(`Persona "${insData.cognome} ${insData.nome}" registrata con successo!`);
        
        // Aggiorna elenco persone
        const updatedList = [...personeList, insData].sort((a, b) => 
          (a.cognome || '').localeCompare(b.cognome || '')
        );
        setPersoneList(updatedList);

        // Pre-seleziona la persona appena inserita nella riga corrente
        if (curMemberIndexForNew !== null) {
          const next = [...editingMembri];
          next[curMemberIndexForNew].id_anagrafica = Number(insData.id);
          setEditingMembri(next);
        }
      }

      // Chiudi e resetta
      setIsCreaNuovaPersonaOpen(false);
      setCurMemberIndexForNew(null);
    } catch (err: any) {
      console.error(err);
      toast.error(`Errore durante la registrazione: ${err.message}`);
    } finally {
      setIsSavingVirtualPersona(false);
    }
  };

  const handleEditFamiglia = async () => {
    try {
      const { data: listData } = await supabase
        .from('cpgev_anagrafica')
        .select('id, cognome, nome, codice_fiscale')
        .order('cognome', { ascending: true });
      if (listData) {
        setPersoneList(listData);
      }
    } catch (e) {
      console.error('Errore nel recupero persone per famiglia:', e);
    }

    if (famiglia) {
      setFamigliaDenominazione(famiglia.denominazione || '');
      const altriMembri = famigliaMembri
        .filter(m => Number(m.id_anagrafica) !== Number(id))
        .map(m => ({
          id_anagrafica: Number(m.id_anagrafica),
          id_parentela: Number(m.id_parentela)
        }));
      setEditingMembri(altriMembri);
    } else {
      setFamigliaDenominazione(data.cognome ? `Famiglia ${data.cognome}` : '');
      setEditingMembri([]);
    }
    setIsEditingFamiglia(true);
  };

  const handleRegistraFamiglia = async () => {
    if (!id) return;
    
    // Controlla che tutti i membri aggiunti siano validi
    for (let i = 0; i < editingMembri.length; i++) {
      if (!editingMembri[i].id_anagrafica) {
        toast.error(`Seleziona una persona valida per il componente ${i + 1}`);
        return;
      }
      if (!editingMembri[i].id_parentela) {
        toast.error(`Seleziona un rapporto di parentela valido per il componente ${i + 1}`);
        return;
      }
    }

    try {
      setSaving(true);
      // Prima proviamo con l'RPC di Supabase
      const { data: rpcData, error: rpcError } = await supabase.rpc('crea_nucleo_familiare', {
        p_id_intestatario: Number(id),
        p_id_parentela_intestatario: 25, // Intestatario scheda
        p_membri: editingMembri,
        p_denominazione: famigliaDenominazione || `Famiglia ${data.cognome}`
      });

      if (rpcError) {
        // Se c'è un errore (es. overload PGRST203), usiamo la nostra API di fallback!
        console.warn('RPC ha sollevato un errore o non è risolvibile. Eseguo fallback su API server...', rpcError);
        const response = await fetch('/api/sys/crea-nucleo-familiare', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_id_intestatario: Number(id),
            p_id_parentela_intestatario: 25,
            p_membri: editingMembri,
            p_denominazione: famigliaDenominazione || `Famiglia ${data.cognome}`,
            p_id_famiglia: famiglia?.id_famiglia // per modifica
          })
        });

        if (!response.ok) {
          const errBody = await response.json();
          throw new Error(errBody.error || 'Errore durante il salvataggio della famiglia');
        }
      }

      toast.success('Stato di famiglia registrato con successo!');
      setIsEditingFamiglia(false);
      fetchFamiglia();
    } catch (e: any) {
      console.error(e);
      toast.error(`Errore nel salvataggio: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const fetchIndirizzi = async () => {
    if (!id || id === 'new') {
      return;
    }
    
    try {
      setLoadingIndirizzi(true);
      
      const personaIdNum = Number(id);
      const { data: indData, error: indError } = await supabase
        .from('cpgev_indirizzi')
        .select('*')
        .eq('id_persona', personaIdNum)
        .order('id', { ascending: true });

      if (indError) {
        console.error('[GESTIONALE] Errore critico query "cpgev_indirizzi":', indError);
        toast.error(`Errore nel caricamento degli indirizzi: ${indError.message}`);
        throw indError;
      }

      if (!indData || indData.length === 0) {
        setIndirizzi([]);
        return;
      }

      const enriched = await Promise.all(indData.map(async (item) => {
        // Tipologia
        let tipologiaDesc = '';
        if (item.id_tipologia_indirizzo) {
          try {
            const { data: tData, error: tError } = await supabase
              .from('cpgev_tipologie_indirizzi')
              .select('descrizione')
              .eq('id', item.id_tipologia_indirizzo)
              .maybeSingle();
            
            if (tError) {
              console.error('[GESTIONALE] Errore nel recupero tipologia indirizzo ID:', item.id_tipologia_indirizzo, tError);
            } else if (tData) {
              tipologiaDesc = tData.descrizione || '';
            }
          } catch (tExc) {
            console.error('[GESTIONALE] Eccezione tipologia indirizzo:', tExc);
          }
        }

        // Comune, Provincia, Regione, Nazione
        let comuneDenom = '';
        let comuneImg = '';
        let provDenom = '';
        let provImg = '';
        let regDenom = '';
        let regImg = '';
        let nazioneDenom = '';
        let nazioneImg = '';

        if (item.id_comune) {
          try {
            const { data: comData, error: comError } = await supabase
              .from('cpgev_comuni')
              .select('denominazione, immagine, id_provincia, id_regione, id_nazione')
              .eq('id', item.id_comune)
              .maybeSingle();

            if (comError) {
              console.error('[GESTIONALE] Errore nel recupero comune ID:', item.id_comune, comError);
            } else if (comData) {
              comuneDenom = comData.denominazione || '';
              comuneImg = comData.immagine || '';

              // Provincia
              if (comData.id_provincia) {
                try {
                  const { data: provData, error: provError } = await supabase
                    .from('cpgev_provincie')
                    .select('denominazione, immagine')
                    .eq('id', comData.id_provincia)
                    .maybeSingle();
                  
                  if (provError) {
                    console.error('[GESTIONALE] Errore nel recupero provincia ID:', comData.id_provincia, provError);
                  } else if (provData) {
                     provDenom = provData.denominazione || '';
                     provImg = provData.immagine || '';
                  }
                } catch (provExc) {
                  console.error('[GESTIONALE] Eccezione provincia:', provExc);
                }
              }

              // Regione
              if (comData.id_regione) {
                try {
                  const { data: regData, error: regError } = await supabase
                    .from('cpgev_regioni')
                    .select('denominazione, immagine')
                    .eq('id', comData.id_regione)
                    .maybeSingle();

                  if (regError) {
                    console.error('[GESTIONALE] Errore nel recupero regione ID:', comData.id_regione, regError);
                  } else if (regData) {
                    regDenom = regData.denominazione || '';
                    regImg = regData.immagine || '';
                  }
                } catch (regExc) {
                  console.error('[GESTIONALE] Eccezione regione:', regExc);
                }
              }

              // Nazione
              if (comData.id_nazione) {
                try {
                  const { data: nazData, error: nazError } = await supabase
                    .from('cpgev_nazioni')
                    .select('denominazione, immagine')
                    .eq('id', comData.id_nazione)
                    .maybeSingle();

                  if (nazError) {
                    console.error('[GESTIONALE] Errore nel recupero nazione ID:', comData.id_nazione, nazError);
                  } else if (nazData) {
                    nazioneDenom = nazData.denominazione || '';
                    nazioneImg = nazData.immagine || '';
                  }
                } catch (nazExc) {
                  console.error('[GESTIONALE] Eccezione nazione:', nazExc);
                }
              }
            }
          } catch (comExc) {
            console.error('[GESTIONALE] Eccezione comune:', comExc);
          }
        }

        return {
          ...item,
          tipologiaDesc,
          comuneDenom,
          comuneImg,
          provDenom,
          provImg,
          regDenom,
          regImg,
          nazioneDenom,
          nazioneImg,
        };
      }));

      setIndirizzi(enriched);
    } catch (e: any) {
      console.error('[GESTIONALE] Errore gestito nel caricamento degli indirizzi:', e);
      toast.error(`Si è verificato un errore durante l'elaborazione degli indirizzi: ${e.message || e}`);
    } finally {
      setLoadingIndirizzi(false);
    }
  };

  useEffect(() => {
    if (!isNew && id) {
      fetchIndirizzi();
    }
  }, [id, isNew]);

  const fetchEmails = async () => {
    if (!id || id === 'new') return;
    try {
      setLoadingEmails(true);
      const { data: emailData, error: emailError } = await supabase
        .from('cpgev_emails')
        .select('*')
        .eq('id_persona', Number(id));

      if (emailError) {
        console.error('Errore nel recupero delle email:', emailError);
        toast.error('Errore nel caricamento delle email: ' + emailError.message);
        return;
      }

      if (emailData) {
        setEmails(emailData);
      }
    } catch (e: any) {
      console.error('Errore fetchEmails:', e);
    } finally {
      setLoadingEmails(false);
    }
  };

  useEffect(() => {
    if (!isNew && id) {
      fetchEmails();
    }
  }, [id, isNew]);

  const handleStartAddEmail = () => {
    setEmailForm({
      id_tipo_mail: '',
      email: '',
      is_pec: false
    });
    setIsAddingEmail(true);
    setEditingEmailId(null);
  };

  const handleStartEditEmail = (em: any) => {
    setEmailForm({
      id_tipo_mail: String(em.id_tipo_mail || ''),
      email: em.email || '',
      is_pec: !!em.is_pec
    });
    setEditingEmailId(em.id);
    setIsAddingEmail(false);
  };

  // Permessi CRUD sulle email
  const checkCrudPermission = (typeId: any) => {
    const numericTypeId = Number(typeId);
    const isSocio = data.tipologia_anagrafica === 1;
    const isUserAdminOrSegreteria = hasGroup('Admins') || hasGroup('app.segreteria.anagrafica.persone');
    const isOwnCard = user?.personaId !== undefined && Number(id) === Number(user.personaId);

    if (isSocio) {
      if (numericTypeId === 99) {
        return isUserAdminOrSegreteria;
      } else if (numericTypeId === 1) {
        return isUserAdminOrSegreteria;
      } else if (numericTypeId >= 2 && numericTypeId <= 98) {
        return isUserAdminOrSegreteria || isOwnCard;
      }
    } else {
      if (numericTypeId === 1 || numericTypeId === 99) {
        return isUserAdminOrSegreteria;
      } else if (numericTypeId >= 2 && numericTypeId <= 98) {
        return isUserAdminOrSegreteria || isOwnCard;
      }
    }
    return false;
  };

  const initiateEmailVerification = async () => {
    try {
      setSaving(true);
      const res = await fetch('/api/email-verification/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailForm.email })
      });
      const resData = await res.json();
      if (!res.ok) {
        throw new Error(resData.error || 'Errore nell\'invio del codice');
      }

      setOtpTokenId(resData.tokenId);
      setOtpEmailTarget(emailForm.email);
      setOtpModalOpen(true);
      setOtpCodeInput('');
      setOtpError('');
      
      toast.success('Codice di verifica inviato all\'email ' + emailForm.email);
      if (resData.debugCode) {
        toast.info(`[DEBUG] Codice verifica (AI Studio): ${resData.debugCode}`, { duration: 15000 });
      }
    } catch (e: any) {
      console.error(e);
      toast.error('Errore nell\'invio del codice: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const doSaveEmailIntoDb = async () => {
    try {
      setSaving(true);
      const payload = {
        id_persona: Number(id),
        id_tipo_mail: Number(emailForm.id_tipo_mail),
        email: emailForm.email,
        is_pec: !!emailForm.is_pec
      };

      let res;
      if (isAddingEmail) {
        res = await fetch('/api/sys/data/cpgev_emails', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else if (editingEmailId) {
        res = await fetch(`/api/sys/data/cpgev_emails/${editingEmailId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (res && !res.ok) {
        const errJson = await res.json();
        throw new Error(errJson.error || 'Errore nel salvataggio della mail');
      }

      toast.success(isAddingEmail ? 'Email aggiunta con successo' : 'Email modificata con successo');
      setIsAddingEmail(false);
      setEditingEmailId(null);
      fetchEmails();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante il salvataggio dell\'email: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!emailForm.id_tipo_mail) {
      toast.error('La tipologia di email è richiesta');
      return;
    }
    if (!emailForm.email) {
      toast.error('L\'indirizzo email è richiesto');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }

    const emType = Number(emailForm.id_tipo_mail);

    const hasPerm = checkCrudPermission(emType);
    if (!hasPerm) {
      toast.error('Non hai i permessi per inserire o modificare email di questa tipologia.');
      return;
    }

    const isUserAdminOrSegreteria = hasGroup('Admins') || hasGroup('app.segreteria.anagrafica.persone');
    const isOwnCard = user?.personaId !== undefined && Number(id) === Number(user.personaId);
    const requiresOtp = !isUserAdminOrSegreteria && isOwnCard && emType >= 2 && emType <= 98;

    if (requiresOtp) {
      setOtpPendingAction('SAVE');
      await initiateEmailVerification();
    } else {
      await doSaveEmailIntoDb();
    }
  };

  const doDeleteEmailFromDb = async (emId: string | number) => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from('cpgev_emails')
        .delete()
        .eq('id', emId);

      if (error) throw error;

      toast.success('Email eliminata con successo');
      fetchEmails();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante l\'eliminazione dell\'email: ' + err.message);
    } finally {
      setSaving(false);
      setDeletingEmailId(null);
      setOtpPendingAction(null);
    }
  };

  const handleDeleteEmail = async (em: any) => {
    const hasPerm = checkCrudPermission(em.id_tipo_mail);
    if (!hasPerm) {
      toast.error('Non hai i permessi per eliminare questa email.');
      return;
    }

    setEmailToDelete(em);
  };

  const handleConfirmDeleteEmail = async () => {
    if (!emailToDelete) return;
    const em = emailToDelete;
    setEmailToDelete(null);

    const isUserAdminOrSegreteria = hasGroup('Admins') || hasGroup('app.segreteria.anagrafica.persone');
    const isOwnCard = user?.personaId !== undefined && Number(id) === Number(user.personaId);
    const requiresOtp = !isUserAdminOrSegreteria && isOwnCard && em.id_tipo_mail >= 2 && em.id_tipo_mail <= 98;
    
    if (requiresOtp) {
      setEmailForm({
        id_tipo_mail: String(em.id_tipo_mail),
        email: em.email,
        is_pec: !!em.is_pec
      });
      setIsAddingEmail(false);
      setEditingEmailId(em.id);
      
      try {
        setSaving(true);
        const res = await fetch('/api/email-verification/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: em.email })
        });
        const resData = await res.json();
        if (!res.ok) throw new Error(resData.error || 'Errore invio codice');

        setOtpTokenId(resData.tokenId);
        setOtpEmailTarget(em.email);
        setOtpModalOpen(true);
        setOtpCodeInput('');
        setOtpError('');
        setOtpPendingAction('DELETE');

        toast.success('Codice di verifica eliminazione inviato all\'email ' + em.email);
        if (resData.debugCode) {
          toast.info(`[DEBUG] Codice verifica (AI Studio): ${resData.debugCode}`, { duration: 15000 });
        }
      } catch (e: any) {
        console.error(e);
        toast.error('Errore nell\'invio del codice: ' + e.message);
      } finally {
        setSaving(false);
      }
    } else {
      await doDeleteEmailFromDb(em.id);
    }
  };

  const handleVerifyOtpAndSave = async () => {
    if (!otpCodeInput || otpCodeInput.length !== 5) {
      setOtpError('Inserisci un codice a 5 cifre.');
      return;
    }

    try {
      setVerifyingOtp(true);
      const res = await fetch('/api/email-verification/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tokenId: otpTokenId, code: otpCodeInput })
      });
      const resData = await res.json();
      if (!res.ok) {
        setOtpError(resData.error || 'Codice non valido o scaduto.');
        return;
      }

      setOtpModalOpen(false);
      toast.success('Email verificata con successo!');
      
      if (otpPendingAction === 'DELETE' && editingEmailId) {
        await doDeleteEmailFromDb(editingEmailId);
      } else {
        await doSaveEmailIntoDb();
      }
    } catch (e: any) {
      console.error(e);
      setOtpError('Errore durante la verifica: ' + e.message);
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Ordinamento emails come richiesto:
  const sortedEmails = useMemo(() => {
    const isSocio = data.tipologia_anagrafica === 1;
    const list = [...emails];
    if (isSocio) {
      list.sort((a, b) => {
        const aType = Number(a.id_tipo_mail);
        const bType = Number(b.id_tipo_mail);
        
        if (aType === 99 && bType !== 99) return -1;
        if (bType === 99 && aType !== 99) return 1;
        return Number(a.id) - Number(b.id);
      });
    } else {
      list.sort((a, b) => Number(a.id) - Number(b.id));
    }
    return list;
  }, [emails, data.tipologia_anagrafica]);

  // Socials fetcher, handlers, and link composition rules
  const fetchSocials = async () => {
    if (!id || id === 'new') return;
    try {
      setLoadingSocials(true);
      const { data: socialData, error: socialError } = await supabase
        .from('cpgev_socials')
        .select('*')
        .eq('id_persona', Number(id));

      if (socialError) {
        console.error('Errore nel recupero dei social:', socialError);
        toast.error('Errore nel caricamento dei social: ' + socialError.message);
        return;
      }

      if (socialData) {
        setSocials(socialData);
      }
    } catch (e: any) {
      console.error('Errore fetchSocials:', e);
    } finally {
      setLoadingSocials(false);
    }
  };

  useEffect(() => {
    if (!isNew && id) {
      fetchSocials();
    }
  }, [id, isNew]);

  const handleStartAddSocial = () => {
    setSocialForm({
      id_social: '',
      username1: '',
      username2: ''
    });
    setIsAddingSocial(true);
    setEditingSocialId(null);
  };

  const handleStartEditSocial = (soc: any) => {
    setSocialForm({
      id_social: String(soc.id_social || ''),
      username1: soc.username1 || '',
      username2: soc.username2 || ''
    });
    setEditingSocialId(soc.id);
    setIsAddingSocial(false);
  };

  const getSocialIcon = (piattaformaName: string) => {
    const norm = (piattaformaName || '').toLowerCase();
    if (norm.includes('facebook')) return <Facebook className="w-4 h-4 text-blue-600 shrink-0" />;
    if (norm.includes('instagram')) return <Instagram className="w-4 h-4 text-pink-600 shrink-0" />;
    if (norm.includes('linkedin')) return <Linkedin className="w-4 h-4 text-blue-700 shrink-0" />;
    if (norm.includes('youtube')) return <Youtube className="w-4 h-4 text-red-600 shrink-0" />;
    if (norm.includes('twitch')) return <Twitch className="w-4 h-4 text-purple-600 shrink-0" />;
    if (norm.includes('telegram')) return <Send className="w-4 h-4 text-sky-500 shrink-0" />;
    if (norm.includes('whatsapp')) return <MessageSquare className="w-4 h-4 text-emerald-500 shrink-0" />;
    if (norm.includes('messenger')) return <MessageCircle className="w-4 h-4 text-blue-500 shrink-0" />;
    if (norm.includes('discord')) return <MessageSquare className="w-4 h-4 text-indigo-500 shrink-0" />;
    if (norm.includes('x (twitter)') || norm === 'x' || norm.includes('twitter')) {
      return <Globe className="w-4 h-4 text-slate-800 shrink-0" />;
    }
    return <Globe className="w-4 h-4 text-slate-500 shrink-0" />;
  };

  const getSocialEmoji = (piattaformaName: string): string => {
    const norm = (piattaformaName || '').toLowerCase();
    if (norm.includes('facebook')) return '🔵';
    if (norm.includes('instagram')) return '📸';
    if (norm.includes('linkedin')) return '💼';
    if (norm.includes('youtube')) return '🎥';
    if (norm.includes('twitch')) return '👾';
    if (norm.includes('telegram')) return '✈️';
    if (norm.includes('whatsapp')) return '🟢';
    if (norm.includes('messenger')) return '💬';
    if (norm.includes('discord')) return '👾';
    if (norm.includes('twitter') || norm === 'x') return '𝕏';
    if (norm.includes('bereal')) return '🤳';
    if (norm.includes('pinterest')) return '📌';
    if (norm.includes('snapchat')) return '👻';
    if (norm.includes('wechat')) return '💬';
    if (norm.includes('tiktok')) return '🎵';
    if (norm.includes('tumblr')) return '📝';
    if (norm.includes('github')) return '💻';
    if (norm.includes('reddit')) return '🤖';
    if (norm.includes('skype')) return '📞';
    return '🌐';
  };

  const getSocialLinks = (soc: any) => {
    const tip = tipologieSocial.find(t => Number(t.id) === Number(soc.id_social));
    if (!tip) return { base: null, alt: null };

    const u1 = soc.username1 || '';
    const u2 = soc.username2 || '';

    // Tumblr (codice 14) special rule: sostituisci $username con username1
    if (Number(soc.id_social) === 14) {
      const baseLink = tip.link_base || '';
      const altLink = tip.link_alternativo || '';
      return {
        base: baseLink.includes('$username') ? baseLink.replace('$username', u1) : `${baseLink}${u1}`,
        alt: altLink ? (altLink.includes('$username') ? altLink.replace('$username', u1) : `${altLink}${u1}`) : null
      };
    }

    // Per altri: concatena username1 e username2
    const suffix = u1 + u2;
    const baseLink = tip.link_base ? `${tip.link_base}${suffix}` : null;
    const altLink = tip.link_alternativo ? `${tip.link_alternativo}${suffix}` : null;
    return { base: baseLink, alt: altLink };
  };

  const handleSaveSocial = async () => {
    if (!socialForm.id_social) {
      toast.error('La piattaforma social è richiesta');
      return;
    }
    if (!socialForm.username1 || !socialForm.username1.trim()) {
      toast.error('Il primo username è obbligatorio');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        id_persona: Number(id),
        id_social: Number(socialForm.id_social),
        username1: socialForm.username1.trim(),
        username2: socialForm.username2 ? socialForm.username2.trim() : null
      };

      if (isAddingSocial) {
        const { error } = await supabase
          .from('cpgev_socials')
          .insert([payload]);
        if (error) throw error;
        toast.success('Social network aggiunto con successo');
      } else if (editingSocialId) {
        const { error } = await supabase
          .from('cpgev_socials')
          .update(payload)
          .eq('id', editingSocialId);
        if (error) throw error;
        toast.success('Social network modificato con successo');
      }

      setIsAddingSocial(false);
      setEditingSocialId(null);
      fetchSocials();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante il salvataggio del social: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSocial = (soc: any) => {
    setSocialToDelete(soc);
  };

  const handleConfirmDeleteSocial = async () => {
    if (!socialToDelete) return;
    const socId = socialToDelete.id;
    setSocialToDelete(null);
    try {
      setSaving(true);
      const { error } = await supabase
        .from('cpgev_socials')
        .delete()
        .eq('id', socId);

      if (error) throw error;

      toast.success('Social network eliminato con successo');
      fetchSocials();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante l\'eliminazione del social: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // --- TELEFONI HANDLERS ---
  const fetchTelefoni = async () => {
    if (!id || id === 'new') return;
    try {
      setLoadingTelefoni(true);
      const { data: telData, error: telError } = await supabase
        .from('cpgev_telefoni')
        .select('*')
        .eq('id_persona', Number(id))
        .order('id', { ascending: true });

      if (telError) {
        console.error('Errore nel recupero dei telefoni:', telError);
        toast.error('Errore nel caricamento dei telefoni: ' + telError.message);
        return;
      }

      if (telData) {
        setTelefoni(telData);
      }
    } catch (e: any) {
      console.error('Errore fetchTelefoni:', e);
    } finally {
      setLoadingTelefoni(false);
    }
  };

  useEffect(() => {
    if (!isNew && id) {
      fetchTelefoni();
    }
  }, [id, isNew]);

  const handleStartAddTelefono = () => {
    setTelefonoForm({
      id_tipoologia_telefono: '',
      numero: '',
      note: ''
    });
    setIsAddingTelefono(true);
    setEditingTelefonoId(null);
  };

  const handleStartEditTelefono = (tel: any) => {
    setTelefonoForm({
      id_tipoologia_telefono: String(tel.id_tipoologia_telefono || ''),
      numero: tel.numero || '',
      note: tel.note || ''
    });
    setEditingTelefonoId(tel.id);
    setIsAddingTelefono(false);
  };

  const handleSaveTelefono = async () => {
    if (!telefonoForm.id_tipoologia_telefono) {
      toast.error('La tipologia di telefono è richiesta');
      return;
    }
    if (!telefonoForm.numero) {
      toast.error('Il numero di telefono è richiesto');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        id_persona: Number(id),
        id_tipoologia_telefono: Number(telefonoForm.id_tipoologia_telefono),
        numero: telefonoForm.numero,
        note: telefonoForm.note || null
      };

      let error;
      if (isAddingTelefono) {
        const { error: insertError } = await supabase
          .from('cpgev_telefoni')
          .insert([payload]);
        error = insertError;
      } else if (editingTelefonoId) {
        const { error: updateError } = await supabase
          .from('cpgev_telefoni')
          .update(payload)
          .eq('id', editingTelefonoId);
        error = updateError;
      }

      if (error) {
        throw error;
      }

      toast.success(isAddingTelefono ? 'Telefono aggiunto con successo' : 'Telefono modificato con successo');
      setIsAddingTelefono(false);
      setEditingTelefonoId(null);
      fetchTelefoni();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante il salvataggio del telefono: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTelefono = (tel: any) => {
    setTelefonoToDelete(tel);
  };

  const handleConfirmDeleteTelefono = async () => {
    if (!telefonoToDelete) return;
    const telId = telefonoToDelete.id;
    setTelefonoToDelete(null);
    try {
      setSaving(true);
      const { error: deleteError } = await supabase
        .from('cpgev_telefoni')
        .delete()
        .eq('id', telId);

      if (deleteError) {
        throw deleteError;
      }

      toast.success('Telefono eliminato con successo');
      fetchTelefoni();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante l\'eliminazione del telefono: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // --- SCHEDA SOCIO HANDLERS & HELPERS ---
  const getDescrizioneStatoSocio = (tipoVal: number | string | null | undefined): string => {
    if (tipoVal === null || tipoVal === undefined || tipoVal === '') return 'Non specificato';
    const found = statiSocioList.find(s => String(s.tipo) === String(tipoVal)) ||
                  DEFAULT_STATI_SOCIO.find(s => String(s.tipo) === String(tipoVal));
    return found?.descrizione || `Stato ${tipoVal}`;
  };

  const getDescrizioneZona = (zonaVal: number | string | null | undefined): string => {
    if (zonaVal === null || zonaVal === undefined || zonaVal === '') return 'Non specificata';
    const found = zoneList.find(z => String(z.id) === String(zonaVal));
    return found?.descrizione || `Zona ${zonaVal}`;
  };

  const fetchSocio = async () => {
    if (!id || id === 'new' || data.tipologia_anagrafica !== 1) {
      setSocioData(null);
      return;
    }
    try {
      setLoadingSocio(true);

      // Assicuriamoci che gli stati socio e zone siano caricati se non ancora pronti
      if (statiSocioList.length === 0) {
        const statiCache = cache.get<any[]>('lookup_stati_socio');
        if (statiCache && Array.isArray(statiCache) && statiCache.length > 0) {
          setStatiSocioList(statiCache);
        } else {
          try {
            const res = await fetch('/api/stati-socio');
            if (res.ok) {
              const apiData = await res.json();
              if (Array.isArray(apiData) && apiData.length > 0) {
                setStatiSocioList(apiData);
                cache.set('lookup_stati_socio', apiData);
              }
            }
          } catch {
            setStatiSocioList(DEFAULT_STATI_SOCIO);
          }
        }
      }

      if (zoneList.length === 0) {
        const zoneCache = cache.get<any[]>('lookup_zone_socio');
        if (zoneCache) {
          setZoneList(zoneCache);
        } else {
          const { data: zoneData } = await supabase
            .from('cpgev_codici_zone')
            .select('id, descrizione')
            .order('id', { ascending: true });
          if (zoneData) {
            setZoneList(zoneData);
            cache.set('lookup_zone_socio', zoneData);
          }
        }
      }

      const { data: sData, error: sErr } = await supabase
        .from('cpgev_soci')
        .select('*')
        .eq('id_anagrafica', Number(id))
        .maybeSingle();

      if (sErr) {
        console.error('Errore nel recupero dati socio:', sErr);
        toast.error('Errore nel caricamento dei dati socio: ' + sErr.message);
        return;
      }

      setSocioData(sData || null);
      if (sData) {
        setSocioForm({
          stato_socio: sData.stato_socio,
          zona: sData.zona,
          matricola: sData.matricola,
          data_iscrizione: sData.data_iscrizione || new Date().toISOString().split('T')[0],
          data_cessazione: sData.data_cessazione || '',
          attributi_aggiuntivi: sData.attributi_aggiuntivi || {}
        });
        setJsonAttributiRaw(JSON.stringify(sData.attributi_aggiuntivi || {}, null, 2));
      }
    } catch (err: any) {
      console.error('Errore fetchSocio:', err);
    } finally {
      setLoadingSocio(false);
    }
  };

  useEffect(() => {
    if (!isNew && id && data.tipologia_anagrafica === 1) {
      fetchSocio();
    } else {
      setSocioData(null);
      setIsEditingSocio(false);
      setIsAddingSocio(false);
    }
  }, [id, isNew, data.tipologia_anagrafica]);

  const handleStartAddSocio = () => {
    const defaultAttributi = serializeAttributiSezioni(DEFAULT_SEZIONI_TEMPLATES);
    setSocioForm({
      stato_socio: statiSocioList.length > 0 ? statiSocioList[0].tipo : 1,
      zona: zoneList.length > 0 ? zoneList[0].id : 8,
      matricola: '',
      data_iscrizione: new Date().toISOString().split('T')[0],
      data_cessazione: '',
      attributi_aggiuntivi: defaultAttributi
    });
    setJsonAttributiRaw(JSON.stringify(defaultAttributi, null, 2));
    setJsonAttributiError('');
    setIsAddingSocio(true);
    setIsEditingSocio(true);
    setIsAccordionSocioOpen(true);
  };

  const handleStartEditSocio = () => {
    if (socioData) {
      const initialAttributi = socioData.attributi_aggiuntivi && Object.keys(socioData.attributi_aggiuntivi).length > 0
        ? socioData.attributi_aggiuntivi
        : serializeAttributiSezioni(DEFAULT_SEZIONI_TEMPLATES);
      setSocioForm({
        stato_socio: socioData.stato_socio,
        zona: socioData.zona,
        matricola: socioData.matricola,
        data_iscrizione: socioData.data_iscrizione || new Date().toISOString().split('T')[0],
        data_cessazione: socioData.data_cessazione || '',
        attributi_aggiuntivi: initialAttributi
      });
      setJsonAttributiRaw(JSON.stringify(initialAttributi, null, 2));
    }
    setJsonAttributiError('');
    setIsAddingSocio(false);
    setIsEditingSocio(true);
    setIsAccordionSocioOpen(true);
  };

  const handleCancelEditSocio = () => {
    setIsEditingSocio(false);
    setIsAddingSocio(false);
    setJsonAttributiError('');
    if (socioData) {
      setSocioForm({
        stato_socio: socioData.stato_socio,
        zona: socioData.zona,
        matricola: socioData.matricola,
        data_iscrizione: socioData.data_iscrizione || '',
        data_cessazione: socioData.data_cessazione || '',
        attributi_aggiuntivi: socioData.attributi_aggiuntivi || {}
      });
      setJsonAttributiRaw(JSON.stringify(socioData.attributi_aggiuntivi || {}, null, 2));
    }
  };

  const handleSaveSocio = async () => {
    if (socioForm.matricola === '' || socioForm.matricola === undefined || socioForm.matricola === null) {
      toast.error('Il numero di matricola / tessera è obbligatorio');
      return;
    }
    const matricolaNum = Number(socioForm.matricola);
    if (isNaN(matricolaNum) || !Number.isInteger(matricolaNum) || matricolaNum <= 0) {
      toast.error('La matricola deve essere un numero intero positivo');
      return;
    }
    if (!socioForm.stato_socio) {
      toast.error('Lo stato socio è obbligatorio');
      return;
    }
    if (socioForm.zona === '' || socioForm.zona === undefined || socioForm.zona === null) {
      toast.error('La zona di appartenenza è obbligatoria');
      return;
    }
    if (!socioForm.data_iscrizione) {
      toast.error('La data di iscrizione è obbligatoria');
      return;
    }

    let parsedAttributi = socioForm.attributi_aggiuntivi || {};
    if (jsonAttributiRaw.trim()) {
      try {
        parsedAttributi = JSON.parse(jsonAttributiRaw);
        setJsonAttributiError('');
      } catch (e: any) {
        setJsonAttributiError('Formato JSON non valido: ' + e.message);
        toast.error('Gli attributi aggiuntivi devono essere in formato JSON valido');
        return;
      }
    }

    try {
      setSavingSocio(true);
      const payload: any = {
        id_anagrafica: Number(id),
        stato_socio: Number(socioForm.stato_socio),
        zona: Number(socioForm.zona),
        matricola: matricolaNum,
        data_iscrizione: socioForm.data_iscrizione,
        data_cessazione: socioForm.data_cessazione || null,
        attributi_aggiuntivi: parsedAttributi,
        data_aggiornamento: new Date().toISOString()
      };

      if (isAddingSocio || !socioData) {
        payload.data_inserimento = new Date().toISOString();
        const { data: newSocio, error: insErr } = await supabase
          .from('cpgev_soci')
          .insert([payload])
          .select()
          .single();

        if (insErr) {
          if (insErr.code === '23505' || insErr.message.includes('uq_numero_tessera') || insErr.message.includes('matricola')) {
            toast.error(`La matricola ${matricolaNum} è già assegnata ad un altro socio.`);
          } else if (insErr.message.includes('uq_socio_anagrafica')) {
            toast.error('Questa persona ha già una scheda socio registrata.');
          } else {
            toast.error('Errore inserimento scheda socio: ' + insErr.message);
          }
          return;
        }

        toast.success('Scheda Socio registrata con successo!');
        setSocioData(newSocio);
        setIsEditingSocio(false);
        setIsAddingSocio(false);
      } else {
        const { data: updSocio, error: updErr } = await supabase
          .from('cpgev_soci')
          .update(payload)
          .eq('id', socioData.id)
          .select()
          .single();

        if (updErr) {
          if (updErr.code === '23505' || updErr.message.includes('uq_numero_tessera') || updErr.message.includes('matricola')) {
            toast.error(`La matricola ${matricolaNum} è già assegnata ad un altro socio.`);
          } else {
            toast.error('Errore aggiornamento scheda socio: ' + updErr.message);
          }
          return;
        }

        toast.success('Scheda Socio aggiornata con successo!');
        setSocioData(updSocio);
        setIsEditingSocio(false);
        setIsAddingSocio(false);
      }
    } catch (err: any) {
      console.error('Errore salvataggio scheda socio:', err);
      toast.error('Errore imprevisto nel salvataggio: ' + err.message);
    } finally {
      setSavingSocio(false);
    }
  };

  const handleConfirmDeleteSocio = async () => {
    if (!socioData) return;
    try {
      setSavingSocio(true);
      const { error: delErr } = await supabase
        .from('cpgev_soci')
        .delete()
        .eq('id', socioData.id);

      if (delErr) {
        toast.error('Errore eliminazione scheda socio: ' + delErr.message);
        return;
      }

      toast.success('Scheda Socio eliminata');
      setSocioData(null);
      setIsEditingSocio(false);
      setIsAddingSocio(false);
      setShowDeleteSocioModal(false);
    } catch (err: any) {
      toast.error('Errore eliminazione scheda socio: ' + err.message);
    } finally {
      setSavingSocio(false);
    }
  };

  const sortedSocials = useMemo(() => {
    return [...socials].sort((a, b) => Number(a.id_social) - Number(b.id_social));
  }, [socials]);

  // Autocomplete per Comuni Indirizzo
  useEffect(() => {
    if (addrComuneSearch.length < 2) {
      setAddrComuniOptions([]);
      return;
    }
    const fetchAddrComuni = async () => {
      setIsSearchingAddrComune(true);
      try {
        const { data: res, error } = await supabase
          .from('cpgev_comuni')
          .select('id, denominazione, immagine, id_provincia, id_regione, id_nazione, cap')
          .ilike('denominazione', `${addrComuneSearch}%`)
          .limit(10);
        if (res && !error) {
          const opts = res.map((c: any) => ({
            id: c.id,
            label: c.denominazione,
            image: c.immagine,
            provId: c.id_provincia,
            regId: c.id_regione,
            nazId: c.id_nazione,
            cap: Array.isArray(c.cap) ? c.cap : (c.cap ? [c.cap] : [])
          }));
          setAddrComuniOptions(opts);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsSearchingAddrComune(false);
      }
    };
    const timer = setTimeout(fetchAddrComuni, 300);
    return () => clearTimeout(timer);
  }, [addrComuneSearch]);

  // Autocomplete per Odonimo (strada) basato sul Comune selezionato
  useEffect(() => {
    if (!addressForm.id_comune || odonimoSearch.length < 1) {
      setOdonimiOptions([]);
      return;
    }
    const fetchOdonimi = async () => {
      setIsSearchingOdonimo(true);
      try {
        const { data: res, error } = await supabase
          .from('cpgev_indirizzario_italia')
          .select('odonimo, progressivo_nazionale, civico, esponente, id_comune')
          .eq('id_comune', addressForm.id_comune)
          .ilike('odonimo', `%${odonimoSearch}%`)
          .limit(30);

        if (res && !error) {
          // preleviamo odonimi univoci
          const uniqueMap: { [key: string]: any } = {};
          res.forEach(item => {
            if (item.odonimo && !uniqueMap[item.odonimo]) {
              uniqueMap[item.odonimo] = item;
            }
          });
          const opts = Object.keys(uniqueMap).map(name => ({
            id: name,
            label: name,
            progressivo_nazionale: uniqueMap[name].progressivo_nazionale,
          }));
          setOdonimiOptions(opts);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsSearchingOdonimo(false);
      }
    };
    const timer = setTimeout(fetchOdonimi, 300);
    return () => clearTimeout(timer);
  }, [odonimoSearch, addressForm.id_comune]);

  // Geocodifica indirizzo per Google Maps
  useEffect(() => {
    if (!selectedMapAddress) return;

    const geocodeAddress = async () => {
      setIsGeocoding(true);
      
      // 0. Controlliamo se ci sono coordinate direttamente nell'indirizzo cpgev_indirizzi (estremamente veloce, zero query esterne)
      if (selectedMapAddress.latitudine !== undefined && selectedMapAddress.latitudine !== null &&
          selectedMapAddress.longitudine !== undefined && selectedMapAddress.longitudine !== null) {
        const lat = Number(selectedMapAddress.latitudine);
        const lng = Number(selectedMapAddress.longitudine);
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          setMapCenter({ lat, lng });
          setIsGeocoding(false);
          return;
        }
      }
      
      // 1. Controlliamo se ci sono coordinate nel db in cpgev_indirizzario_italia
      if (selectedMapAddress.progressivo_nazionale) {
        try {
          const { data } = await supabase
            .from('cpgev_indirizzario_italia')
            .select('latitudine, longitudine')
            .eq('progressivo_nazionale', Number(selectedMapAddress.progressivo_nazionale))
            .maybeSingle();
          if (data?.latitudine && data?.longitudine) {
            setMapCenter({ lat: Number(data.latitudine), lng: Number(data.longitudine) });
            setIsGeocoding(false);
            return;
          }
        } catch (e) {
          console.error(e);
        }
      }

      // 2. Geocodifica client-side solo come fallback finale se non ci sono coordinate salvate
      if (typeof window !== 'undefined' && (window as any).google?.maps) {
        try {
          const geocoder = new (window as any).google.maps.Geocoder();
          const addressStr = `${selectedMapAddress.odonimo || ''} ${selectedMapAddress.civico || ''}${selectedMapAddress.esponente ? '/' + selectedMapAddress.esponente : ''}, ${selectedMapAddress.comuneDenom || ''}, Italy`;
          
          geocoder.geocode({ address: addressStr }, (results: any, status: any) => {
            if (status === 'OK' && results?.[0]?.geometry?.location) {
              const loc = results[0].geometry.location;
              setMapCenter({ lat: loc.lat(), lng: loc.lng() });
            } else {
              console.error('Geocodifica fallita:', status);
              setMapCenter({ lat: 44.4949, lng: 11.3426 }); // Default Bologna
            }
            setIsGeocoding(false);
          });
        } catch (err) {
          console.error(err);
          setMapCenter({ lat: 44.4949, lng: 11.3426 });
          setIsGeocoding(false);
        }
      } else {
        setMapCenter({ lat: 44.4949, lng: 11.3426 });
        setIsGeocoding(false);
      }
    };

    geocodeAddress();
  }, [selectedMapAddress]);

  const handleStartAddAddress = () => {
    setAddressForm({
      id_tipologia_indirizzo: '',
      id_comune: '',
      cap: '',
      odonimo: '',
      civico: '',
      esponente: '',
      progressivo_nazionale: '',
      latitudine: '',
      longitudine: ''
    });
    setAddrComuneSearch('');
    setOdonimoSearch('');
    setIsAddingAddress(true);
    setEditingAddressId(null);
  };

  const handleStartEditAddress = (addr: any) => {
    setAddressForm({
      id_tipologia_indirizzo: String(addr.id_tipologia_indirizzo || ''),
      id_comune: addr.id_comune || '',
      cap: String(addr.cap || ''),
      odonimo: addr.odonimo || '',
      civico: addr.civico || '',
      esponente: addr.esponente || '',
      progressivo_nazionale: String(addr.progressivo_nazionale || ''),
      latitudine: addr.latitudine !== undefined && addr.latitudine !== null ? String(addr.latitudine) : '',
      longitudine: addr.longitudine !== undefined && addr.longitudine !== null ? String(addr.longitudine) : ''
    });
    setAddrComuneSearch(addr.comuneDenom || '');
    setOdonimoSearch(addr.odonimo || '');
    setEditingAddressId(addr.id);
    setIsAddingAddress(false);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.id_tipologia_indirizzo) {
      toast.error('La tipologia di indirizzo è richiesta');
      return;
    }
    if (!addressForm.id_comune) {
      toast.error('Il Comune è richiesto');
      return;
    }
    if (!addressForm.odonimo) {
      toast.error('L\'odonimo (via/piazza) è richiesto');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        id_persona: Number(id),
        id_tipologia_indirizzo: Number(addressForm.id_tipologia_indirizzo),
        id_comune: addressForm.id_comune,
        cap: addressForm.cap ? Number(addressForm.cap) : null,
        odonimo: addressForm.odonimo,
        civico: addressForm.civico || null,
        esponente: addressForm.esponente || null,
        progressivo_nazionale: addressForm.progressivo_nazionale || null,
        latitudine: addressForm.latitudine ? parseFloat(String(addressForm.latitudine).replace(',', '.')) : null,
        longitudine: addressForm.longitudine ? parseFloat(String(addressForm.longitudine).replace(',', '.')) : null
      };

      if (isAddingAddress) {
        const { error } = await supabase.from('cpgev_indirizzi').insert([payload]);
        if (error) throw error;
        toast.success('Indirizzo aggiunto con successo');
      } else if (editingAddressId) {
        const { error } = await supabase.from('cpgev_indirizzi').update(payload).eq('id', editingAddressId);
        if (error) throw error;
        toast.success('Indirizzo aggiornato con successo');
      }

      setIsAddingAddress(false);
      setEditingAddressId(null);
      fetchIndirizzi();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante il salvataggio dell\'indirizzo: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = (addr: any) => {
    setAddressToDelete(addr);
  };

  const handleConfirmDeleteAddress = async () => {
    if (!addressToDelete) return;
    const addrId = addressToDelete.id;
    setAddressToDelete(null);
    try {
      setSaving(true);
      const { error } = await supabase
        .from('cpgev_indirizzi')
        .delete()
        .eq('id', addrId);

      if (error) throw error;
      
      toast.success('Indirizzo eliminato con successo');
      if (selectedMapAddress?.id === addrId) {
        setSelectedMapAddress(null);
      }
      fetchIndirizzi();
    } catch (err: any) {
      console.error(err);
      toast.error('Errore durante l\'eliminazione dell\'indirizzo: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMarkerDrag = async () => {
    if (!selectedMapAddress || !proposedCoords) return;
    try {
      setIsUpdatingCoords(true);
      const { error } = await supabase
        .from('cpgev_indirizzi')
        .update({
          latitudine: parseFloat(String(proposedCoords.lat)),
          longitudine: parseFloat(String(proposedCoords.lng))
        })
        .eq('id', selectedMapAddress.id);

      if (error) throw error;
      toast.success('Coordinate aggiornate sulla mappa!');
      
      // Aggiorna l'indirizzo locale per riflettere le modifiche
      setSelectedMapAddress((prev: any) =>
        prev ? { ...prev, latitudine: proposedCoords.lat, longitudine: proposedCoords.lng } : null
      );
      setMapCenter(proposedCoords);
      setProposedCoords(null);
      fetchIndirizzi();
    } catch (err: any) {
      console.error('Errore nel salvataggio delle coordinate:', err);
      toast.error('Errore nel salvataggio delle coordinate: ' + err.message);
    } finally {
      setIsUpdatingCoords(false);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setUseCamera(true);
      }
    } catch (err) {
      toast.error('Impossibile accedere alla fotocamera');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      setUseCamera(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0);
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'photo.webp', { type: 'image/webp' });
            setPhotoFile(file);
            setPhotoPreview(URL.createObjectURL(file));
            stopCamera();
          }
        }, 'image/webp', 0.8);
      }
    }
  };

  const uploadPhoto = async (cognome: string, nome: string) => {
    if (!photoFile) return null;
    const path = getPersonPhotoPath(cognome, nome); 
    
    try {
      // Convertiamo il file in base64 per inviarlo tramite il proxy server-side
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // Rimuove il prefisso data:image/...;base64,
        };
        reader.onerror = reject;
        reader.readAsDataURL(photoFile);
      });

      const response = await fetch('/api/storage/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          path,
          content: base64Content,
          contentType: photoFile.type || 'image/webp'
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Errore durante l\'upload tramite proxy');
      }
        
      return path;
    } catch (err) {
      console.error('Photo upload error:', err);
      toast.error('Errore caricamento foto');
      return null;
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWrite) return;
    if (!data.cognome || !data.nome) {
      toast.error('Cognome e Nome sono obbligatori');
      return;
    }

    if (data.tipologia_anagrafica === 1) {
      const missing = [];
      if (!data.titolo_onorifico) missing.push('Titolo');
      if (!data.cognome) missing.push('Cognome');
      if (!data.nome) missing.push('Nome');
      if (!data.codice_fiscale) missing.push('Codice Fiscale');
      if (!data.sesso) missing.push('Sesso');
      if (!data.data_nascita) missing.push('Data Nascita');
      if (!data.id_nazione_nascita) missing.push('Nazione');
      if (!data.id_comune_nascita) missing.push('Comune');
      if (!data.titolo_studio) missing.push('Titolo di Studio');
      
      if (missing.length > 0) {
        toast.error(`Per i soci CPGEV sono obbligatori: ${missing.join(', ')}`);
        return;
      }
    }

    if (data.codice_fiscale && !validaCodiceFiscale(data.codice_fiscale)) {
      toast.error('Il Codice Fiscale inserito non è valido o formattato correttamente');
      return;
    }

    if (data.codice_fiscale && data.data_nascita && data.sesso && data.cognome && data.nome) {
      const congruenza = verificaCongruenzaCF(
        data.codice_fiscale, 
        data.data_nascita, 
        data.sesso, 
        data.cognome, 
        data.nome, 
        belfioreCode || undefined
      );
      if (!congruenza.valid) {
        toast.error(congruenza.message);
        return;
      }
    }

    try {
      setSaving(true);
      if (photoFile) await uploadPhoto(data.cognome, data.nome);

      const { id: _, foto: __, avatar_url: ___, ...payload } = data;
      
      // Rimuoviamo esplicitamente campi foto se dovessero essere scappati
      delete (payload as any).foto;
      delete (payload as any).avatar_url;
      delete (payload as any).image;
      delete (payload as any).picture;
      
      // Pulizia payload: convertiamo stringhe vuote in null per i campi opzionali
      // e assicuriamo che i campi numerici siano effettivamente numeri
      const numericFields = [
        'titolo_onorifico', 
        'id_nazione_nascita', 
        'id_regione_nascita', 
        'id_provincia_nascita', 
        'id_comune_nascita', 
        'tipologia_anagrafica', 
        'titolo_studio'
      ];

      Object.keys(payload).forEach(key => {
        if (payload[key] === '') {
          payload[key] = null;
        } else if (numericFields.includes(key) && payload[key] !== null) {
          const val = Number(payload[key]);
          payload[key] = isNaN(val) ? null : val;
        }
      });

      let res;
      if (isNew) {
        res = await supabase.from('cpgev_anagrafica').insert([payload]).select();
      } else {
        // Aggiorna l'utente che effettua l'aggiornamento
        payload.aggiornato_da = (await supabase.auth.getUser()).data.user?.id;
        payload.data_aggiornamento = new Date().toISOString();
        res = await supabase.from('cpgev_anagrafica').update(payload).eq('id', id).select();
      }

      if (res.error) throw res.error;
      toast.success(isNew ? 'Aggiunto' : 'Salvato');
      
      // Se è nuovo, dobbiamo navigare al nuovo ID con action=view
      // Se è esistente, rimaniamo qui con action=view
      const finalId = isNew ? res.data?.[0]?.id : id;
      if (finalId) {
        navigate(`/segreteria/anagrafica/persone/${finalId}?action=view`);
        if (!isNew) fetchPersona(); // Forza rilettura se eravamo già sul record
      } else {
        navigate('/segreteria/anagrafica/persone');
      }
    } catch (error: any) {
      toast.error('Errore salvataggio');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canWrite || isNew) return;
    try {
      setSaving(true);
      const { error } = await supabase.from('cpgev_anagrafica').delete().eq('id', id);
      if (error) throw error;
      toast.success('Eliminato');
      navigate('/segreteria/anagrafica/persone');
    } catch (error: any) {
      toast.error('Errore eliminazione');
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
    }
  };

  const getRequiredClass = (field: string) => {
    if (data.tipologia_anagrafica === 1 && isEditing) {
      const mandatoryFields = ['titolo_onorifico', 'cognome', 'nome', 'codice_fiscale', 'sesso', 'id_nazione_nascita', 'id_comune_nascita', 'titolo_studio'];
      if (mandatoryFields.includes(field)) {
        return "border-blue-700 ring-1 ring-blue-700/20"; 
      }
    }
    return "border-slate-200";
  };

  if (!canRead) return <div className="p-8 text-center text-red-500">Accesso Negato</div>;
  if (loading) return <div className="p-8 text-center animate-pulse">Caricamento...</div>;

  const charCount = data.note?.length || 0;
  const maxNoteChars = 4096;

  return (
    <div className="w-full py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <button 
          onClick={() => navigate('/segreteria/anagrafica/persone')}
          className="flex items-center text-sm text-muted-foreground hover:text-primary transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Elenco
        </button>
        
        <div className="flex gap-2">
          {!isEditing && canWrite && (
            <button 
              onClick={() => navigate(`/segreteria/anagrafica/persone/${id}?action=edit`)}
              className="px-4 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-lg text-sm font-medium hover:bg-primary/20 transition-all"
            >
              Modifica
            </button>
          )}
          {isEditing && (
            <>
              <button 
                ref={cancelBtnRef}
                onClick={() => isNew ? navigate('/segreteria/anagrafica/persone') : navigate(`/segreteria/anagrafica/persone/${id}`)}
                className="px-4 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 transition-all focus:ring-2 focus:ring-primary focus:ring-offset-2 outline-none"
              >
                Annulla
              </button>
              <button 
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? '...' : 'Salva'}
              </button>
            </>
          )}
          {!isNew && canWrite && (
            <button 
              onClick={() => {
                setConfirmText('');
                setShowDeleteModal(true);
              }}
              disabled={saving}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all"
              title="Cancella persona!"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-3xl shadow-sm overflow-hidden">
        <div className="p-6">
          {/* RIGA 01: TITOLO CENTRATO */}
          <div className="flex flex-col items-center justify-center gap-1.5 mb-6 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h1 className="text-lg font-black uppercase tracking-[0.25em] text-slate-900 italic">DATI ANAGRAFICI</h1>
          </div>

          {/* RIGA 02: FOTO E INFO PRINCIPALI */}
          <div className="flex flex-col md:flex-row gap-6">
            {/* FOTO A SINISTRA */}
            <div className="flex-shrink-0">
              <div className="relative w-[150px] h-[190px] bg-slate-50 rounded-2xl overflow-hidden border-2 border-slate-100 shadow-inner flex items-center justify-center group">
                {useCamera ? (
                  <div className="absolute inset-0 z-20 bg-black">
                    <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                    <div className="absolute bottom-2 inset-x-0 flex justify-center gap-2">
                       <button type="button" onClick={capturePhoto} className="p-2 bg-white rounded-full shadow-lg"><Camera className="w-4 h-4" /></button>
                       <button type="button" onClick={stopCamera} className="p-2 bg-red-500 text-white rounded-full"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ) : photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <SignedImage 
                    path={getPersonPhotoPath(data.cognome, data.nome)}
                    className="w-full h-full object-cover"
                    fallback={<User className="w-12 h-12 text-slate-200" />}
                  />
                )}

                {isEditing && canWrite && !useCamera && (
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <label className="p-2 bg-white rounded-full cursor-pointer hover:scale-110 transition-transform">
                      <Upload className="w-4 h-4" />
                      <input type="file" accept="image/*" className="hidden" onChange={handlePhotoSelect} />
                    </label>
                    <button type="button" onClick={startCamera} className="p-2 bg-white rounded-full hover:scale-110"><Camera className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>

            {/* INFO PRINCIPALI A DESTRA */}
            <div className="flex-grow flex flex-col gap-4">
              {/* RIGA 01: ID, Ruolo, Titolo, Cognome, Nome, Titolo Studio */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* ID - Read-only ALWAYS */}
                <div className="col-span-12 md:col-span-2 lg:col-span-1">
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1 tracking-wider">ID</label>
                  <input 
                    type="text"
                    disabled
                    value={id || '-'}
                    className="w-full px-3 py-1.5 bg-slate-100/70 border rounded-xl text-xs font-mono font-bold text-slate-500 text-center outline-none min-h-[36px]"
                  />
                </div>

                {/* Tipologia Anagrafica */}
                <div className="col-span-12 md:col-span-3 lg:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1 tracking-wider">Tipologia</label>
                  <select 
                    disabled={!isEditing || !canWrite}
                    value={data.tipologia_anagrafica || 1}
                    onChange={e => setData({...data, tipologia_anagrafica: Number(e.target.value)})}
                    className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-primary appearance-none italic font-bold text-slate-700 min-h-[36px]"
                  >
                    {tipologieAnagrafiche.length > 0 ? (
                      tipologieAnagrafiche.map(t => (
                        <option key={t.id} value={t.id}>{t.descrizione}</option>
                      ))
                    ) : (
                      <>
                        <option value={1}>Socio CPGEV</option>
                        <option value={2}>Collaboratore / Persona Fisica</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Titolo */}
                <div className="col-span-12 md:col-span-2 lg:col-span-1">
                  <ComboBox 
                    label="Titolo"
                    options={titoli}
                    value={data.titolo_onorifico}
                    disabled={!isEditing || !canWrite}
                    inputClassName={getRequiredClass('titolo_onorifico')}
                    onChange={val => setData({...data, titolo_onorifico: val})}
                  />
                </div>

                {/* Cognome */}
                <div className="col-span-12 md:col-span-5 lg:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block tracking-wider">Cognome</label>
                  <input 
                    type="text"
                    required
                    disabled={!isEditing || !canWrite}
                    value={data.cognome || ''}
                    onChange={e => setData({...data, cognome: e.target.value})}
                    onBlur={e => setData({...data, cognome: formatName(e.target.value)})}
                    className={cn(
                      "w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm font-black text-slate-900 focus:ring-1 focus:ring-primary outline-none disabled:bg-transparent transition-all min-h-[36px]",
                      getRequiredClass('cognome')
                    )}
                  />
                </div>

                {/* Nome */}
                <div className="col-span-12 md:col-span-5 lg:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block tracking-wider">Nome</label>
                  <input 
                    type="text"
                    required
                    disabled={!isEditing || !canWrite}
                    value={data.nome || ''}
                    onChange={e => setData({...data, nome: e.target.value})}
                    onBlur={e => {
                      const formatted = formatName(e.target.value);
                      setData({...data, nome: formatted});
                      checkGenderCongruence(formatted, data.sesso);
                    }}
                    className={cn(
                      "w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm font-black text-slate-900 focus:ring-1 focus:ring-primary outline-none disabled:bg-transparent transition-all min-h-[36px]",
                      getRequiredClass('nome')
                    )}
                  />
                </div>

                {/* Titolo di Studio */}
                <div className="col-span-12 md:col-span-7 lg:col-span-4">
                  <ComboBox 
                    label="Titolo di Studio"
                    placeholder="Filtra titolo studio..."
                    options={titoliStudio}
                    value={data.titolo_studio}
                    disabled={!isEditing || !canWrite}
                    inputClassName={getRequiredClass('titolo_studio')}
                    onChange={val => setData({...data, titolo_studio: val})}
                  />
                </div>
              </div>

              {/* RIGA 02: C.F., ID ANPR, P.IVA, Sesso */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* Codice Fiscale */}
                <div className="col-span-12 md:col-span-4">
                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block tracking-wider">Codice Fiscale</label>
                  <input 
                    type="text"
                    disabled={!isEditing || !canWrite}
                    value={data.codice_fiscale || ''}
                    onChange={e => setData({...data, codice_fiscale: e.target.value.toUpperCase()})}
                    onBlur={validateCFOnBlur}
                    className={cn(
                      "w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm font-mono text-primary focus:ring-1 focus:ring-primary outline-none disabled:bg-transparent transform transition-all min-h-[36px]",
                      getRequiredClass('codice_fiscale')
                    )}
                  />
                </div>

                {/* ID ANPR */}
                <div className="col-span-12 md:col-span-3">
                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block tracking-wider">ID ANPR</label>
                  <input 
                    type="text"
                    disabled={!isEditing || !canWrite}
                    value={data.id_anpr || ''}
                    onChange={e => setData({...data, id_anpr: e.target.value.toUpperCase()})}
                    maxLength={9}
                    className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm font-mono focus:ring-1 focus:ring-primary outline-none disabled:bg-transparent min-h-[36px]"
                    placeholder="Esempio: ID1234567"
                  />
                </div>

                {/* Partita IVA */}
                <div className="col-span-12 md:col-span-3">
                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block tracking-wider">Partita IVA</label>
                  <input 
                    type="text"
                    disabled={!isEditing || !canWrite}
                    value={data.partita_iva || ''}
                    onChange={e => setData({...data, partita_iva: e.target.value})}
                    className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm focus:ring-1 focus:ring-primary outline-none disabled:bg-transparent min-h-[36px]"
                  />
                </div>

                {/* Sesso */}
                <div className="col-span-12 md:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block tracking-wider">Sesso</label>
                  <select 
                    disabled={!isEditing || !canWrite}
                    value={data.sesso || 'M'}
                    onChange={e => {
                      setData({...data, sesso: e.target.value});
                      checkGenderCongruence(data.nome, e.target.value);
                    }}
                    className={cn(
                      "w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none appearance-none disabled:bg-transparent transition-all min-h-[36px]",
                      getRequiredClass('sesso')
                    )}
                  >
                    <option value="M">Maschio</option>
                    <option value="F">Femmina</option>
                    <option value="A">Altro</option>
                  </select>
                </div>
              </div>

              {/* RIGA 03: Data Nascita + Luogo di Nascita */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                {/* Data Nascita */}
                <div className="col-span-12 md:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-slate-400 mb-1 block tracking-wider">Data Nascita</label>
                  <div className="space-y-1">
                    <input 
                      type="date"
                      disabled={!isEditing || !canWrite}
                      value={data.data_nascita || ''}
                      onChange={e => setData({...data, data_nascita: e.target.value})}
                      className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none disabled:bg-transparent min-h-[36px]"
                    />
                    {data.data_nascita && (
                      <div className="text-[9px] text-slate-500 italic px-1">
                        ({getDayOfWeek(data.data_nascita)})
                      </div>
                    )}
                  </div>
                </div>

                {/* Nazione */}
                <div className="col-span-12 md:col-span-2">
                  {!isEditing ? (
                    <>
                      <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1 tracking-wider">
                        <Lock className="w-2.5 h-2.5 text-amber-500" />
                        <span>Nazione</span>
                      </label>
                      <div className="px-3 py-1.5 bg-slate-100/50 border rounded-xl text-xs font-medium text-slate-800 min-h-[36px] flex items-center justify-between shadow-inner gap-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {decodedLabels.nazioneImmagine && (
                            <SignedImage path={decodedLabels.nazioneImmagine} className="w-4 h-3 object-contain flex-shrink-0" />
                          )}
                          <span className="truncate">{decodedLabels.nazione || '-'}</span>
                        </div>
                        <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      </div>
                    </>
                  ) : (
                    <ComboBox 
                      label="Nazione"
                      placeholder="Cerca Nazione..."
                      options={nazioni}
                      value={data.id_nazione_nascita}
                      showImage
                      disabled={!canWrite}
                      inputClassName={getRequiredClass('id_nazione_nascita')}
                      onChange={val => {
                        setData({
                          ...data, 
                          id_nazione_nascita: val, 
                          id_regione_nascita: null, 
                          id_provincia_nascita: null, 
                          id_comune_nascita: null
                        });
                        setComuneSearch('');
                        setBelfioreCode(null);
                      }}
                    />
                  )}
                </div>

                {/* Comune */}
                <div className="col-span-12 md:col-span-3">
                  {!isEditing ? (
                    <>
                      <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1 tracking-wider">
                        <Lock className="w-2.5 h-2.5 text-amber-500" />
                        <span>Comune</span>
                      </label>
                      <div className="px-3 py-1.5 bg-slate-100/50 border rounded-xl text-xs font-black text-slate-900 min-h-[36px] flex items-center justify-between shadow-inner gap-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {decodedLabels.comuneImmagine && (
                            <SignedImage path={decodedLabels.comuneImmagine} className="w-4 h-3 object-contain flex-shrink-0" />
                          )}
                          <span className="truncate">{decodedLabels.comune || '-'}</span>
                        </div>
                        <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      </div>
                    </>
                  ) : (
                    (() => {
                      const comuniOptions = [...comuni];
                      if (data.id_comune_nascita && decodedLabels.comune) {
                        const exists = comuniOptions.some(c => c.id === data.id_comune_nascita);
                        if (!exists) {
                          comuniOptions.unshift({
                            id: data.id_comune_nascita,
                            label: decodedLabels.comune,
                            image: decodedLabels.comuneImmagine,
                            belfiore: data.id_comune_nascita,
                            provId: data.id_provincia_nascita,
                            regId: data.id_regione_nascita
                          });
                        }
                      }
                      
                      return (
                        <ComboBox 
                          label="Comune"
                          placeholder="Cerca Comune..."
                          options={comuniOptions}
                          value={data.id_comune_nascita}
                          showImage
                          isLoading={isSearchingComune}
                          disabled={!canWrite || !data.id_nazione_nascita}
                          inputClassName={getRequiredClass('id_comune_nascita')}
                          onSearchChange={setComuneSearch}
                          onChange={handleComuneChange}
                        />
                      );
                    })()
                  )}
                </div>

                {/* Regione */}
                <div className="col-span-12 md:col-span-3">
                  <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1 tracking-wider">
                    <Lock className="w-2.5 h-2.5 text-amber-500" />
                    <span>Regione</span>
                  </label>
                  <div className="px-3 py-1.5 bg-slate-100/50 border rounded-xl text-xs text-slate-500 min-h-[36px] flex items-center font-bold justify-between gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {decodedLabels.regioneImmagine && (
                        <SignedImage path={decodedLabels.regioneImmagine} className="w-4 h-3 object-contain flex-shrink-0" />
                      )}
                      <span className="truncate">{decodedLabels.regione || '-'}</span>
                    </div>
                    <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  </div>
                </div>

                {/* Provincia */}
                <div className="col-span-12 md:col-span-2">
                  <label className="text-[10px] uppercase font-bold text-slate-500 mb-1 flex items-center gap-1 tracking-wider">
                    <Lock className="w-2.5 h-2.5 text-amber-500" />
                    <span>Provincia</span>
                  </label>
                  <div className="px-3 py-1.5 bg-slate-100/50 border rounded-xl text-xs text-slate-500 min-h-[36px] flex items-center font-bold justify-between gap-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {decodedLabels.provinciaImmagine && (
                        <SignedImage path={decodedLabels.provinciaImmagine} className="w-4 h-3 object-contain flex-shrink-0" />
                      )}
                      <span className="truncate">{decodedLabels.provincia || '-'}</span>
                    </div>
                    <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  </div>
                </div>
              </div>

              {/* RIGA 04: Note Personali */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-1">
                <div className="col-span-12">
                  <div className="flex justify-between items-end mb-1">
                    <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Note Personali / Storico</label>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full ${charCount > maxNoteChars ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
                      {charCount} / {maxNoteChars}
                    </span>
                  </div>
                  <textarea 
                    disabled={!isEditing || !canWrite}
                    value={data.note || ''}
                    onChange={e => setData({...data, note: e.target.value.substring(0, maxNoteChars)})}
                    rows={4}
                    className="w-full px-4 py-2 bg-slate-50 border rounded-2xl text-sm outline-none resize-none focus:ring-1 focus:ring-primary h-24 transition-all"
                    placeholder="Inserisci dettagli, note o storico per questa persona..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEZIONI COMPLEMENTARI: STATO DI FAMIGLIA E INDIRIZZI */}
      {!isNew && (
        <div id="grid-sezioni-complementari" className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 pb-6">
          {/* STATO DI FAMIGLIA */}
          <div id="card-stato-famiglia" className="bg-white border rounded-3xl shadow-sm overflow-hidden transition-all h-fit">
            {/* Accordion Trigger Header */}
            <div
              onClick={() => setIsAccordionFamigliaOpen(!isAccordionFamigliaOpen)}
              className="w-full flex items-center justify-between p-6 hover:bg-slate-50/50 transition-colors text-left cursor-pointer select-none"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 text-primary rounded-xl">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800 tracking-tight">Stato di famiglia</h3>
                  <p className="text-xs text-slate-400 font-semibold mt-0.5">
                    {loadingFamiglia 
                      ? 'Caricamento...' 
                      : famiglia 
                      ? `${famigliaMembri.length} componenti`
                      : 'Nessun nucleo familiare'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <input
                  type="file"
                  ref={pdfFileInputRef}
                  accept="application/pdf"
                  onChange={handlePDFUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => pdfFileInputRef.current?.click()}
                  disabled={parsingPDF}
                  className="p-1.5 px-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 active:scale-95"
                >
                  {parsingPDF ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-indigo-600" />
                  ) : (
                    <FileText className="w-3.5 h-3.5" />
                  )}
                  <span>{parsingPDF ? 'Analisi...' : 'Importa PDF'}</span>
                </button>

                {!isEditingFamiglia && (
                  <button 
                    type="button"
                    onClick={handleEditFamiglia}
                    className="p-1.5 px-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl text-xs font-bold transition-all flex items-center gap-1 active:scale-95"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{famiglia ? 'Modifica' : 'Nuova famiglia'}</span>
                  </button>
                )}

                <motion.div
                  animate={{ rotate: isAccordionFamigliaOpen ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="p-1 px-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors ml-1 cursor-pointer"
                  onClick={() => setIsAccordionFamigliaOpen(!isAccordionFamigliaOpen)}
                >
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {isAccordionFamigliaOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="border-t border-slate-100 p-6 bg-slate-50/30">
              {loadingFamiglia ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                </div>
              ) : (
                <>
                  {/* PROPOSTA PDF AI (BACCHETTA MAGICA) */}
                  {parsedPDFData && (
                    <div className="mb-6 p-5 bg-indigo-50/50 rounded-2xl border border-indigo-100 flex flex-col gap-4 text-xs font-semibold">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-indigo-750 font-black uppercase text-[10px] tracking-wider">
                          <Sparkles className="w-4 h-4 text-indigo-500 animate-pulse" />
                          <span>Verifiche e Proposta Stato di Famiglia dal PDF</span>
                        </div>
                        <button
                          type="button"
                          onClick={handleApplyMagicProposal}
                          className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black flex items-center gap-1 shadow-sm transition-all cursor-pointer"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Applica con Bacchetta Magica
                        </button>
                      </div>

                      {parsedPDFData.isFallback && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2 text-[11px] text-amber-800 font-medium">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <strong className="font-extrabold block mb-0.5">⚠️ SIMULAZIONE AD ALTA FEDELTÀ</strong>
                            {parsedPDFData.fallbackReason || "La quota di crediti Gemini è momentaneamente esaurita."} L'AI ha generato i dati in modalità simulata basata sul layout reale del documento d'esempio per consentirti di testare gli allineamenti, le verifiche anagrafiche e la bacchetta magica.
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-slate-600 border-b border-indigo-100 pb-3">
                        <div className="space-y-3">
                          <div>
                            <span className="block text-[9px] uppercase text-slate-400 font-bold tracking-wider">1. Indirizzo Famiglia Proposto</span>
                            <span className="text-xs font-black text-slate-800">{parsedPDFData.indirizzo_residenza || 'Non rilevato'}</span>
                          </div>
                          <div>
                            <span className="block text-[9px] uppercase text-slate-400 font-bold tracking-wider">2. Intestatario della Scheda Rilevato</span>
                            <span className="text-xs font-black text-slate-800 block">
                              {parsedPDFData.intestatario?.cognome} {parsedPDFData.intestatario?.nome}
                              {parsedPDFData.intestatario?.data_nascita && ` (nato il ${parsedPDFData.intestatario.data_nascita}`}
                              {parsedPDFData.intestatario?.comune_nascita && ` a ${parsedPDFData.intestatario.comune_nascita}`}
                              {parsedPDFData.intestatario?.provincia_nascita && ` - ${parsedPDFData.intestatario.provincia_nascita})`}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[9px] uppercase text-slate-400 font-bold tracking-wider">3. Denominazione / Estremi Atto</span>
                            <span className="text-xs font-bold text-slate-700 block italic bg-white p-2 rounded-xl border border-slate-150 mt-1">
                              {parsedPDFData.estremi_atto || 'Atto non rilevato'}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {/* Verifica Congruenza Indirizzo */}
                          <div>
                            <span className="block text-[9px] uppercase text-slate-400 font-bold tracking-wider">4. Verifica Congruenza Indirizzo</span>
                            <div className="flex gap-2 items-start mt-1 bg-white p-2 rounded-xl border border-slate-150">
                              {parsedPDFData.congruenza_indirizzo?.valido ? (
                                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                              )}
                              <span className="text-xs text-slate-700 font-bold leading-normal">{parsedPDFData.congruenza_indirizzo?.spiegazione}</span>
                            </div>
                          </div>

                          {/* Verifica Congruenza CF */}
                          <div>
                            <span className="block text-[9px] uppercase text-slate-400 font-bold tracking-wider">5. Verifica Congruenza Codice Fiscale</span>
                            <div className="flex gap-2 items-start mt-1 bg-white p-2 rounded-xl border border-slate-150">
                              {parsedPDFData.congruenza_codice_fiscale?.valido ? (
                                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                              )}
                              <span className="text-xs text-slate-700 font-bold leading-normal">{parsedPDFData.congruenza_codice_fiscale?.spiegazione}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {parsedPDFData.componenti && parsedPDFData.componenti.length > 0 && (
                        <div>
                          <span className="block text-[9px] uppercase text-slate-400 font-bold tracking-wider mb-2">6. Componenti Familiari Estratti</span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {parsedPDFData.componenti.map((c: any, index: number) => {
                              const exists = personeList.some((p: any) => 
                                p.cognome?.trim().toUpperCase() === c.cognome?.trim().toUpperCase() &&
                                p.nome?.trim().toUpperCase() === c.nome?.trim().toUpperCase()
                              );
                              return (
                                <div key={index} className="p-2 bg-white rounded-xl border border-slate-150 flex items-center justify-between">
                                  <div>
                                    <div className="font-extrabold text-slate-800 text-[11px]">{c.cognome} {c.nome}</div>
                                    <span className="text-[10px] text-slate-500 font-bold">
                                      {c.rapporto_parentela} {c.data_nascita ? `· ${c.data_nascita}` : ''}
                                    </span>
                                  </div>
                                  <div>
                                    {exists ? (
                                      <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded-md text-[8px] uppercase font-bold tracking-wide">Nel Database</span>
                                    ) : (
                                      <span className="px-1.5 py-0.5 bg-amber-55 text-amber-600 rounded-md text-[8px] uppercase font-bold tracking-wide">✨ Da Registrare</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {isEditingFamiglia ? (
                    // FORM DI MODIFICA / CREAZIONE
                    <div className="space-y-4">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1 tracking-wider">Denominazione Nucleo Familiare</label>
                    <input 
                      type="text" 
                      value={famigliaDenominazione}
                      onChange={e => setFamigliaDenominazione(e.target.value)}
                      className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-primary text-slate-800 font-bold"
                      placeholder="Es: Famiglia Rossi"
                    />
                  </div>

                  <div className="space-y-3 mt-4">
                    <label className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Membri del Nucleo</label>
                    
                    {/* Intestatario fisso (Persona corrente) */}
                    <div className="flex flex-col sm:flex-row gap-3 items-center p-3 bg-slate-50 rounded-2xl border border-slate-100">
                      <div className="flex-1 min-w-0">
                        <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider">Intestatario</span>
                        <span className="text-sm font-extrabold text-slate-800">{data.cognome} {data.nome} (Soggetto corrente)</span>
                      </div>
                      <div className="w-full sm:w-48">
                        <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider">Parentela</span>
                        <span className="text-xs font-bold text-slate-600 italic">Intestatario scheda</span>
                      </div>
                      <div className="w-8 flex shrink-0"></div>
                    </div>

                    {/* Altri membri */}
                    {editingMembri.map((member, index) => (
                      <div key={index} className="flex flex-col sm:flex-row gap-3 items-start p-3 bg-white rounded-2xl border border-slate-200">
                        {/* Seleziona Persona */}
                        <div className="flex-1 min-w-0 w-full">
                          <label className="text-[9px] uppercase font-bold text-slate-400 block mb-1 tracking-wider">Componente {index + 1}</label>
                          <select
                            value={member.id_anagrafica || ''}
                            onChange={e => {
                              const val = e.target.value;
                              if (val === 'NEW_VIRTUAL') {
                                setCurMemberIndexForNew(index);
                                if (member.pdf_info) {
                                  const comp = member.pdf_info;
                                  setNuovaPersonaForm({
                                    cognome: comp.cognome || '',
                                    nome: comp.nome || '',
                                    codice_fiscale: comp.codice_fiscale || '',
                                    sesso: comp.sesso || 'M',
                                    data_nascita: comp.data_nascita || '',
                                    comune_nascita: comp.comune_nascita || '',
                                    provincia_nascita: comp.provincia_nascita || ''
                                  });
                                } else {
                                  setNuovaPersonaForm({
                                    cognome: '',
                                    nome: '',
                                    codice_fiscale: '',
                                    sesso: 'M',
                                    data_nascita: '',
                                    comune_nascita: '',
                                    provincia_nascita: ''
                                  });
                                }
                                setIsCreaNuovaPersonaOpen(true);
                                return;
                              }
                              const next = [...editingMembri];
                              next[index].id_anagrafica = Number(val);
                              setEditingMembri(next);
                            }}
                            className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-xs outline-none focus:ring-1 focus:ring-primary text-slate-800 font-medium animate-pulse-once"
                          >
                            <option value="">-- Seleziona Persona --</option>
                            <option value="NEW_VIRTUAL" className="font-extrabold text-indigo-600 bg-indigo-50">✨ Nuova persona...</option>
                            {personeList
                              .filter(p => Number(p.id) !== Number(id))
                              .map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.cognome} {p.nome} {p.codice_fiscale ? `(${p.codice_fiscale})` : ''}
                                </option>
                              ))}
                          </select>
                        </div>

                        {/* Seleziona Parentela */}
                        <div className="w-full sm:w-48">
                          <label className="text-[9px] uppercase font-bold text-slate-400 block mb-1 tracking-wider">Relazione di Parentela</label>
                          <select
                            value={member.id_parentela || ''}
                            onChange={e => {
                              const val = Number(e.target.value);
                              const next = [...editingMembri];
                              next[index].id_parentela = val;
                              setEditingMembri(next);
                            }}
                            className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-xs outline-none focus:ring-1 focus:ring-primary text-slate-800 font-medium"
                          >
                            <option value="">-- Seleziona --</option>
                            {parentele
                              .filter(p => Number(p.id) !== 25)
                              .map(p => (
                                <option key={p.id} value={p.id}>{p.descrizione}</option>
                              ))}
                          </select>
                        </div>

                        {/* Elimina riga */}
                        <div className="w-8 flex shrink-0 justify-center sm:pt-4">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingMembri(editingMembri.filter((_, idx) => idx !== index));
                            }}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Pulsanti azioni */}
                    <div className="flex gap-3 pt-3">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingMembri([...editingMembri, { id_anagrafica: 0, id_parentela: 0 }]);
                        }}
                        className="flex-1 py-1 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Altro componente
                      </button>
                      <button
                        type="button"
                        onClick={handleRegistraFamiglia}
                        disabled={saving}
                        className="flex-1 py-1 px-3 bg-primary hover:bg-primary/95 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1 shadow-sm"
                      >
                        Registra famiglia
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsEditingFamiglia(false)}
                      className="w-full mt-1.5 py-1.5 border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-xl text-xs font-bold transition-all"
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              ) : (
                // VISUALIZZAZIONE NORMALE
                <div>
                  {famiglia ? (
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100/60 flex items-center justify-between text-xs">
                        <div>
                          <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider">Denominazione</span>
                          <span className="font-extrabold text-slate-800">{famiglia.denominazione || `Famiglia ${data.cognome}`}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-[9px] uppercase font-bold text-slate-400 block tracking-wider">Data Inserimento</span>
                          <span className="font-mono text-slate-550 font-bold">
                            {famiglia.data_inserimento ? new Date(famiglia.data_inserimento).toLocaleDateString('it-IT') : '-'}
                          </span>
                        </div>
                      </div>

                      <div className="border border-slate-100 rounded-2xl overflow-hidden bg-white shadow-sm overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-slate-50/70 border-b border-slate-100 text-left text-[10px]">
                              <th className="p-3 font-bold text-slate-400 tracking-wider uppercase">Membro</th>
                              <th className="p-3 font-bold text-slate-400 tracking-wider uppercase">Relazione</th>
                              <th className="p-3 font-bold text-slate-400 tracking-wider uppercase">Codice Fiscale</th>
                              <th className="p-3 font-bold text-slate-400 tracking-wider uppercase text-right">Azioni</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {[...famigliaMembri]
                              .sort((a, b) => {
                                if (Number(a.id_parentela) === 25) return -1;
                                if (Number(b.id_parentela) === 25) return 1;
                                return Number(a.id_anagrafica) - Number(b.id_anagrafica);
                              })
                              .map((m) => {
                                const p = m.cpgev_anagrafica;
                                const r = m.cpgev_parentele;
                                const isCurrent = Number(p?.id) === Number(id);
                                
                                return (
                                  <tr key={m.id} className={cn("hover:bg-slate-50/50 transition-colors text-xs font-semibold text-slate-705", isCurrent && "bg-primary/5 hover:bg-primary/10")}>
                                    <td className="p-3 font-bold text-slate-900">
                                      {p ? `${p.cognome} ${p.nome}` : '-'} 
                                      {isCurrent && <span className="text-[9px] px-1.5 py-0.2 bg-primary/20 text-primary rounded-full font-bold ml-1.5">Tu</span>}
                                    </td>
                                    <td className="p-3 text-slate-600 italic font-medium">
                                      {r?.descrizione || `ID: ${m.id_parentela}`}
                                    </td>
                                    <td className="p-3 font-mono text-slate-500 font-bold">
                                      {p?.codice_fiscale || '-'}
                                    </td>
                                    <td className="p-3 text-right">
                                      {!isCurrent && p?.id && (
                                        <button
                                          type="button"
                                          onClick={() => navigate(`/segreteria/anagrafica/persone/${p.id}`)}
                                          className="p-1 px-2.5 text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors font-bold text-xs"
                                        >
                                          Vedi
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                      <Users className="w-8 h-8 text-slate-300 mb-2" />
                      <span className="text-xs text-slate-400 font-bold px-4">
                        Nessuna famiglia definita per {data.cognome} {data.nome}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>

          <div className="bg-white border rounded-3xl shadow-sm overflow-hidden transition-all h-fit">
          {/* Accordion Trigger Header */}
          <button
            type="button"
            onClick={() => setIsAccordionOpen(!isAccordionOpen)}
            className="w-full flex items-center justify-between p-6 hover:bg-slate-50/50 transition-colors text-left focus:outline-none"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 text-primary rounded-xl">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight">Indirizzi</h3>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">
                  {indirizzi.length === 0
                    ? 'Nessun indirizzo associato'
                    : indirizzi.length === 1
                    ? '1 indirizzo registrato'
                    : `${indirizzi.length} indirizzi registrati`}
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: isAccordionOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="p-1 px-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </button>

          <AnimatePresence initial={false}>
            {isAccordionOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="border-t border-slate-100 p-6 bg-slate-50/30">
                  {/* Outer Grid for Side-by-Side Map view */}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    
                    {/* Left Column: Address List OR Add/Edit Form */}
                    <div className={cn(
                      "col-span-12 transition-all duration-300",
                      selectedMapAddress ? "lg:col-span-7" : "lg:col-span-12"
                    )}>
                      {isAddingAddress || editingAddressId ? (
                        /* Address Form */
                        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">
                          <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                            <Plus className="w-4 h-4 text-primary" />
                            <span>{isAddingAddress ? 'Nuovo Indirizzo' : 'Modifica Indirizzo'}</span>
                          </h4>

                          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            {/* Tipologia Indirizzo Combobox */}
                            <div className="col-span-12 md:col-span-4">
                              <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Tipologia Indirizzo</label>
                              <select
                                value={addressForm.id_tipologia_indirizzo || ''}
                                onChange={e => setAddressForm({ ...addressForm, id_tipologia_indirizzo: e.target.value })}
                                className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-primary appearance-none italic font-bold text-slate-700 min-h-[36px]"
                              >
                                <option value="">Seleziona...</option>
                                {tipologieIndirizzi.map(t => (
                                  <option key={t.id} value={t.id}>{t.descrizione}</option>
                                ))}
                              </select>
                            </div>

                            {/* Comune ComboBox */}
                            <div className="col-span-12 md:col-span-8">
                              {(() => {
                                const comOpts = [...addrComuniOptions];
                                if (addressForm.id_comune) {
                                  const curComObj = comOpts.find(c => c.id === addressForm.id_comune);
                                  if (!curComObj) {
                                    // Try to resolve current label
                                    const match = indirizzi.find(i => i.id_comune === addressForm.id_comune);
                                    comOpts.unshift({
                                      id: addressForm.id_comune,
                                      label: match ? match.comuneDenom : addressForm.id_comune,
                                      image: match ? match.comuneImg : ''
                                    });
                                  }
                                }

                                return (
                                  <ComboBox
                                    label="Comune"
                                    placeholder="Cerca Comune..."
                                    options={comOpts}
                                    value={addressForm.id_comune}
                                    showImage
                                    isLoading={isSearchingAddrComune}
                                    onSearchChange={setAddrComuneSearch}
                                    onChange={(val) => {
                                      const selected = addrComuniOptions.find(c => c.id === val);
                                      if (selected) {
                                        const defaultCap = selected.cap && selected.cap.length > 0 ? selected.cap[0] : '';
                                        setAddressForm((prev: any) => ({
                                          ...prev,
                                          id_comune: selected.id,
                                          cap: defaultCap || prev.cap || ''
                                        }));
                                      } else {
                                        setAddressForm((prev: any) => ({
                                          ...prev,
                                          id_comune: val
                                        }));
                                      }
                                      setOdonimoSearch('');
                                      setOdonimiOptions([]);
                                    }}
                                  />
                                );
                              })()}
                            </div>

                            {/* CAP */}
                            <div className="col-span-12 md:col-span-3">
                              <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">CAP</label>
                              <input
                                type="text"
                                value={addressForm.cap || ''}
                                onChange={e => setAddressForm({ ...addressForm, cap: e.target.value })}
                                className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-primary min-h-[36px]"
                              />
                            </div>

                            {/* Odonimo ComboBox */}
                            <div className="col-span-12 md:col-span-9">
                              {(() => {
                                const odOpts = [...odonimiOptions];
                                if (addressForm.odonimo) {
                                  const exists = odOpts.some(o => o.id === addressForm.odonimo);
                                  if (!exists) {
                                    odOpts.unshift({
                                      id: addressForm.odonimo,
                                      label: addressForm.odonimo
                                    });
                                  }
                                }

                                return (
                                  <ComboBox
                                    label="Odonimo (Via, Piazza, ...)"
                                    placeholder="Digita o seleziona odonimo..."
                                    options={odOpts}
                                    value={addressForm.odonimo}
                                    isLoading={isSearchingOdonimo}
                                    onSearchChange={setOdonimoSearch}
                                    onChange={(val) => {
                                      const selected = odonimiOptions.find(o => o.id === val);
                                      if (selected) {
                                        setAddressForm((prev: any) => ({
                                          ...prev,
                                          odonimo: selected.id,
                                          progressivo_nazionale: selected.progressivo_nazionale ? String(selected.progressivo_nazionale) : prev.progressivo_nazionale
                                        }));
                                      } else {
                                        setAddressForm((prev: any) => ({
                                          ...prev,
                                          odonimo: val
                                        }));
                                      }
                                    }}
                                  />
                                );
                              })()}
                            </div>

                            {/* Civico */}
                            <div className="col-span-12 md:col-span-4">
                              <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Civico</label>
                              <input
                                type="text"
                                value={addressForm.civico || ''}
                                onChange={e => setAddressForm({ ...addressForm, civico: e.target.value })}
                                className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-primary min-h-[36px]"
                              />
                            </div>

                            {/* Esponente */}
                            <div className="col-span-12 md:col-span-3">
                              <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Esponente</label>
                              <input
                                type="text"
                                placeholder="..."
                                value={addressForm.esponente || ''}
                                onChange={e => setAddressForm({ ...addressForm, esponente: e.target.value })}
                                className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-primary min-h-[36px]"
                              />
                            </div>

                            {/* Progressivo Nazionale */}
                            <div className="col-span-12 md:col-span-4">
                              <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Progressivo Nazionale</label>
                              <input
                                type="text"
                                value={addressForm.progressivo_nazionale || ''}
                                onChange={e => setAddressForm({ ...addressForm, progressivo_nazionale: e.target.value })}
                                className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-xs font-mono text-slate-600 outline-none focus:ring-1 focus:ring-primary min-h-[36px]"
                              />
                            </div>

                            {/* Latitudine */}
                            <div className="col-span-12 md:col-span-4">
                              <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Latitudine</label>
                              <input
                                type="text"
                                placeholder="Esempio: 44.4949"
                                value={addressForm.latitudine || ''}
                                onChange={e => setAddressForm({ ...addressForm, latitudine: e.target.value })}
                                className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-xs font-mono text-slate-600 outline-none focus:ring-1 focus:ring-primary min-h-[36px]"
                              />
                            </div>

                            {/* Longitudine */}
                            <div className="col-span-12 md:col-span-4">
                              <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Longitudine</label>
                              <input
                                type="text"
                                placeholder="Esempio: 11.3426"
                                value={addressForm.longitudine || ''}
                                onChange={e => setAddressForm({ ...addressForm, longitudine: e.target.value })}
                                className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-xs font-mono text-slate-600 outline-none focus:ring-1 focus:ring-primary min-h-[36px]"
                              />
                            </div>
                          </div>

                          <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
                            <button
                              type="button"
                              onClick={() => {
                                setIsAddingAddress(false);
                                setEditingAddressId(null);
                              }}
                              className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
                            >
                              Annulla
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveAddress}
                              disabled={saving}
                              className="px-5 py-2 bg-primary text-white rounded-xl text-xs font-bold hover:shadow-md transition-colors"
                            >
                              {saving ? 'Salvataggio...' : 'Salva Indirizzo'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Address List */
                        <div className="space-y-4">
                          <div className="flex justify-between items-center bg-white border border-slate-100/80 rounded-2xl p-4 shadow-xs">
                            <span className="text-xs font-black uppercase text-slate-400 tracking-wider">
                              Elenco Indirizzi
                            </span>
                            {canWrite && (
                              <button
                                type="button"
                                onClick={handleStartAddAddress}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary border border-primary/20 rounded-xl text-xs font-bold hover:bg-primary/20 transition-all flex-shrink-0"
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Aggiungi Indirizzo</span>
                              </button>
                            )}
                          </div>

                          {loadingIndirizzi ? (
                            <div className="p-10 text-center text-slate-400 text-sm animate-pulse">
                              Caricamento indirizzi...
                            </div>
                          ) : indirizzi.length === 0 ? (
                            <div className="p-8 text-center bg-white border border-dashed rounded-2xl text-slate-400 text-sm">
                              Nessun indirizzo inserito per questa persona. Clicca su "Aggiungi Indirizzo" per registrarne uno.
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {indirizzi.map((item) => (
                                <div
                                  key={item.id}
                                  className={cn(
                                    "border rounded-2xl p-4 bg-white hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4",
                                    selectedMapAddress?.id === item.id ? "border-primary ring-2 ring-primary/5" : "border-slate-100"
                                  )}
                                >
                                  {/* Info */}
                                  <div className="space-y-1.5 min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">
                                        ID IN_ {item.id}
                                      </span>
                                      {item.tipologiaDesc && (
                                        <span className="text-xs font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600">
                                          {item.tipologiaDesc}
                                        </span>
                                      )}
                                      {item.progressivo_nazionale && (
                                        <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold">
                                          PN: {item.progressivo_nazionale}
                                        </span>
                                      )}
                                    </div>

                                    <p className="text-sm font-black text-slate-800 tracking-tight">
                                      {item.odonimo} {item.civico}
                                      {item.esponente ? `/${item.esponente}` : ''}
                                      {item.cap ? `, CAP ${item.cap}` : ''}
                                    </p>

                                    {/* Geolocalization / Decodification badging */}
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                      {item.comuneDenom && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded-full text-[10px] font-medium text-slate-600">
                                          {item.comuneImg && <SignedImage path={item.comuneImg} className="w-3.5 h-2.5 object-contain" />}
                                          <span>{item.comuneDenom}</span>
                                        </span>
                                      )}
                                      {item.provDenom && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded-full text-[10px] font-medium text-slate-500">
                                          {item.provImg && <SignedImage path={item.provImg} className="w-3.5 h-2.5 object-contain" />}
                                          <span>{item.provDenom}</span>
                                        </span>
                                      )}
                                      {item.regDenom && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded-full text-[10px] font-medium text-slate-500">
                                          {item.regImg && <SignedImage path={item.regImg} className="w-3.5 h-2.5 object-contain" />}
                                          <span>{item.regDenom}</span>
                                        </span>
                                      )}
                                      {item.nazioneDenom && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded-full text-[10px] font-medium text-slate-500">
                                          {item.nazioneImg && <SignedImage path={item.nazioneImg} className="w-3.5 h-2.5 object-contain" />}
                                          <span>{item.nazioneDenom}</span>
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-1.5 flex-shrink-0 w-full md:w-auto border-t md:border-t-0 pt-3 md:pt-0 justify-end">
                                    <button
                                      type="button"
                                      onClick={() => setSelectedMapAddress(selectedMapAddress?.id === item.id ? null : item)}
                                      className={cn(
                                        "inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold transition-all",
                                        selectedMapAddress?.id === item.id
                                          ? "bg-slate-900 text-white shadow-xs"
                                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                      )}
                                    >
                                      <MapIcon className="w-3.5 h-3.5" />
                                      <span>{selectedMapAddress?.id === item.id ? 'Chiudi mappa' : 'Mappa'}</span>
                                    </button>
                                    
                                    {canWrite && (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => handleStartEditAddress(item)}
                                          className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors border border-slate-100"
                                          title="Modifica"
                                        >
                                          <Edit className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteAddress(item)}
                                          className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors border border-red-100"
                                          title="Elimina"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Right Column: Google Maps (Side or bottom) */}
                    {selectedMapAddress && (
                      <div className="col-span-12 lg:col-span-5 bg-white border border-slate-100 rounded-3xl p-5 shadow-sm space-y-4 select-none">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Navigation className="w-4 h-4 text-primary animate-pulse" />
                            {(() => {
                              const lat = selectedMapAddress.latitudine || mapCenter?.lat;
                              const lng = selectedMapAddress.longitudine || mapCenter?.lng;
                              let mapsUrl = '';
                              if (lat && lng) {
                                mapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
                              } else {
                                const addressStr = encodeURIComponent(`${selectedMapAddress.odonimo || ''} ${selectedMapAddress.civico || ''}, ${selectedMapAddress.comuneDenom || ''}, Italia`);
                                mapsUrl = `https://www.google.com/maps/search/?api=1&query=${addressStr}`;
                              }
                              return (
                                <a
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-black text-xs uppercase tracking-wider text-primary hover:text-primary-hover focus:outline-none transition-colors cursor-pointer flex items-center gap-1 group"
                                  title="Apri in Google Maps"
                                >
                                  <span>Continua in Google Maps</span>
                                  <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
                                </a>
                              );
                            })()}
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedMapAddress(null)}
                            className="p-1 hover:bg-slate-100 rounded-lg transition-colors text-slate-400"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {isGeocoding ? (
                          <div className="h-[320px] rounded-2xl bg-slate-50 flex items-center justify-center border border-slate-100 animate-pulse text-xs text-slate-400">
                            Geolocalizzazione in corso...
                          </div>
                        ) : (
                          (() => {
                            const API_KEY =
                              mapsApiKey ||
                              process.env.GOOGLE_MAPS_PLATFORM_KEY ||
                              (import.meta as any).env?.VITE_GOOGLE_MAPS_PLATFORM_KEY ||
                              (globalThis as any).GOOGLE_MAPS_PLATFORM_KEY ||
                              '';
                            const hasValidKey = Boolean(API_KEY) && API_KEY !== 'YOUR_API_KEY';

                            if (!hasValidKey) {
                              return (
                                <div className="h-[320px] rounded-2xl bg-slate-50 flex flex-col items-center justify-center border border-slate-100 p-6 text-center">
                                  <div className="p-3 bg-amber-50 text-amber-500 rounded-full mb-3">
                                    <MapIcon className="w-6 h-6" />
                                  </div>
                                  <h5 className="font-bold text-slate-700 text-sm mb-1">Integrazione Google Maps</h5>
                                  <p className="text-xs text-slate-400 max-w-xs mb-4">
                                    Configura l'API Key <code>GOOGLE_MAPS_PLATFORM_KEY</code> nei Segreti di AI Studio per visualizzare la mappa interattiva.
                                  </p>
                                  <div className="text-[10px] text-slate-500 italic space-y-1">
                                    <p>Indirizzo da mostrare:</p>
                                    <p className="font-semibold text-slate-700 bg-slate-100 px-3 py-1 rounded-lg">
                                      {selectedMapAddress.odonimo} {selectedMapAddress.civico}, {selectedMapAddress.comuneDenom}
                                    </p>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div className="space-y-2 text-left">
                                <div className="h-[320px] w-full rounded-2xl overflow-hidden border border-slate-100 shadow-inner relative">
                                  <APIProvider apiKey={API_KEY} version="weekly">
                                    <GMap
                                      key={selectedMapAddress?.id}
                                      defaultCenter={mapCenter || { lat: 44.4949, lng: 11.3426 }}
                                      defaultZoom={15}
                                      mapId="DEMO_MAP_ID"
                                      internalUsageAttributionIds={['gmp_mcp_codeassist_v1_aistudio']}
                                      style={{ width: '100%', height: '100%' }}
                                    >
                                      {mapCenter && (
                                        <AdvancedMarker
                                          position={proposedCoords || mapCenter}
                                          draggable={canWrite}
                                          onDragEnd={(e: any) => {
                                            const newLat = e.latLng?.lat();
                                            const newLng = e.latLng?.lng();
                                            if (typeof newLat === 'number' && typeof newLng === 'number') {
                                              setProposedCoords({ lat: newLat, lng: newLng });
                                            }
                                          }}
                                          title={`${selectedMapAddress.tipologiaDesc || 'Indirizzo'} di ${data.nome || ''} ${data.cognome || ''}`}
                                        >
                                          <Pin background={proposedCoords ? "#EA580C" : "#2563EB"} glyphColor="#fff" />
                                        </AdvancedMarker>
                                      )}
                                    </GMap>
                                  </APIProvider>
                                  
                                  {/* Info floating box on the map or coordinates update card */}
                                  {proposedCoords ? (
                                    <div className="absolute inset-x-3 bottom-3 bg-amber-500/95 backdrop-blur-md text-white p-3.5 rounded-xl border border-amber-400 shadow-xl text-left text-xs z-10 flex flex-col gap-2">
                                      <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 text-white shrink-0 animate-bounce mt-0.5" />
                                        <div className="space-y-0.5 flex-1 select-text">
                                          <strong className="text-white block font-black uppercase tracking-wider text-[10px]">
                                            Sposta Posizione Marker
                                          </strong>
                                          <p className="text-white/90 text-[10px] leading-relaxed">
                                            Hai spostato la coordinata dell'indirizzo. Vuoi registrare la nuova posizione?
                                          </p>
                                          <div className="text-[10px] font-mono text-amber-100 pt-0.5 font-semibold">
                                            {proposedCoords.lat.toFixed(6)}, {proposedCoords.lng.toFixed(6)}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center justify-end gap-1.5 pt-1.5 border-t border-white/25">
                                        <button
                                          type="button"
                                          onClick={() => setProposedCoords(null)}
                                          disabled={isUpdatingCoords}
                                          className="px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-white font-bold transition-all text-[11px] outline-none active:scale-95"
                                        >
                                          Annulla
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleSaveMarkerDrag}
                                          disabled={isUpdatingCoords}
                                          className="px-3 py-1 bg-white text-amber-600 rounded-lg font-bold shadow-xs hover:bg-amber-50 active:scale-95 transition-all text-[11px] outline-none flex items-center gap-1"
                                        >
                                          {isUpdatingCoords ? (
                                            <>
                                              <span className="w-3 h-3 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></span>
                                              <span>Salvataggio...</span>
                                            </>
                                          ) : (
                                            <span>Salva Nuova Posizione</span>
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-xs p-3 rounded-xl border border-slate-100 shadow-lg text-left text-xs pointer-events-none">
                                      <strong className="text-primary truncate block uppercase text-[10px] font-black tracking-wider">
                                        {selectedMapAddress.tipologiaDesc || 'Indirizzo'} di {data.nome || ''} {data.cognome || ''}
                                      </strong>
                                      <span className="text-slate-600 block transition-all font-semibold truncate pt-0.5">
                                        {selectedMapAddress.odonimo} {selectedMapAddress.civico}{selectedMapAddress.esponente ? `/${selectedMapAddress.esponente}` : ''}
                                      </span>
                                      <span className="text-slate-400 text-[10px] block truncate text-slate-500">
                                        {selectedMapAddress.latitudine !== undefined && selectedMapAddress.latitudine !== null && selectedMapAddress.longitudine !== undefined && selectedMapAddress.longitudine !== null ? (
                                          <span className="font-mono text-[9px] text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-100/40 inline-block mr-1">
                                            Coord: {Number(selectedMapAddress.latitudine).toFixed(5)}, {Number(selectedMapAddress.longitudine).toFixed(5)}
                                          </span>
                                        ) : null}
                                        {selectedMapAddress.comuneDenom} ({selectedMapAddress.provDenom}), Italia
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50/50 border border-emerald-100/40 rounded-xl text-[10px] text-slate-500 font-medium">
                                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                                  <span>
                                    <strong>Istruzioni Mappa:</strong> Trascina il marker blu con il mouse per correggere di precisione la posizione geografica. Ti verrà richiesto di salvarla.
                                  </span>
                                </div>
                              </div>
                            );
                          })()
                        )}
                      </div>
                    )}

                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          {/* ACCORDION TELEFONI */}
          <div id="accordion-telefoni" className="bg-white border rounded-3xl shadow-sm overflow-hidden transition-all h-fit">
          {/* Accordion Trigger Header */}
          <button
            id="btn-accordion-telefoni-trigger"
            type="button"
            onClick={() => setIsAccordionTelefoniOpen(!isAccordionTelefoniOpen)}
            className="w-full flex items-center justify-between p-6 hover:bg-slate-50/50 transition-colors text-left focus:outline-none"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight">Telefoni</h3>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">
                  {telefoni.length === 0
                    ? 'Nessun numero telefonico associato'
                    : telefoni.length === 1
                    ? '1 numero telefonico registrato'
                    : `${telefoni.length} numeri telefonici registrati`}
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: isAccordionTelefoniOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="p-1 px-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </button>

          <AnimatePresence initial={false}>
            {isAccordionTelefoniOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="border-t border-slate-100 p-6 bg-slate-50/30">
                  <div className="space-y-6">
                    {isAddingTelefono || editingTelefonoId ? (
                      /* Telefono Form */
                      <div id="phone-form-container" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">
                        <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                          <Plus className="w-4 h-4 text-emerald-600" />
                          <span>{isAddingTelefono ? 'Nuovo Telefono' : 'Modifica Telefono'}</span>
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                          {/* Tipologia Telefono select */}
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Tipologia</label>
                            <select
                              id="select-telefono-type"
                              value={telefonoForm.id_tipoologia_telefono || ''}
                              onChange={e => setTelefonoForm({ ...telefonoForm, id_tipoologia_telefono: e.target.value })}
                              className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-emerald-600 appearance-none italic font-bold text-slate-700 min-h-[36px]"
                            >
                              <option value="">Seleziona...</option>
                              {tipologieTelefoni.map(t => (
                                <option key={t.id} value={t.id}>{t.tipologia_telefono}</option>
                              ))}
                            </select>
                          </div>

                          {/* Numero Input */}
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Numero</label>
                            <input
                              id="input-telefono-number"
                              type="tel"
                              placeholder="Fissi o cellulari (es. +39333...)"
                              value={telefonoForm.numero || ''}
                              onChange={e => setTelefonoForm({ ...telefonoForm, numero: e.target.value })}
                              className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-emerald-600 min-h-[36px]"
                            />
                          </div>

                          {/* Note Input */}
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Note / Descrizione</label>
                            <input
                              id="input-telefono-note"
                              type="text"
                              placeholder="Es. Solo orari lavorativi, reperibilità..."
                              value={telefonoForm.note || ''}
                              onChange={e => setTelefonoForm({ ...telefonoForm, note: e.target.value })}
                              className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-emerald-600 min-h-[36px]"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
                          <button
                            id="btn-cancel-telefono"
                            type="button"
                            onClick={() => {
                              setIsAddingTelefono(false);
                              setEditingTelefonoId(null);
                            }}
                            className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
                          >
                            Annulla
                          </button>
                          <button
                            id="btn-save-telefono"
                            type="button"
                            onClick={handleSaveTelefono}
                            disabled={saving}
                            className="px-5 py-2 bg-emerald-600 text-white rounded-xl text-xs font-bold hover:shadow-md transition-colors"
                          >
                            {saving ? 'Salvataggio...' : 'Salva Telefono'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Telefono List */
                      <div className="space-y-4">
                        <div id="telefono-list-header" className="flex justify-between items-center bg-white border border-slate-100/80 rounded-2xl p-4 shadow-xs">
                          <span className="text-xs font-black uppercase text-slate-400 tracking-wider">
                            Elenco Recapiti Telefonici
                          </span>
                          {(canWrite || isOwnCard) && (
                            <button
                              id="btn-add-telefono-trigger"
                              type="button"
                              onClick={handleStartAddTelefono}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-xl text-xs font-bold hover:bg-emerald-100 transition-all flex-shrink-0"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span>Aggiungi Numero</span>
                            </button>
                          )}
                        </div>

                        {loadingTelefoni ? (
                          <div className="p-10 text-center text-slate-400 text-sm animate-pulse">
                            Caricamento telefoni...
                          </div>
                        ) : telefoni.length === 0 ? (
                          <div className="p-8 text-center bg-white border border-dashed rounded-2xl text-slate-400 text-sm">
                            Nessun numero di telefono inserito per questa persona. Clicca su "Aggiungi Numero" per registrarne uno.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {telefoni.map((item) => {
                              const tipoName = tipologieTelefoni.find(t => Number(t.id) === Number(item.id_tipoologia_telefono))?.tipologia_telefono || `Tipo ${item.id_tipoologia_telefono}`;
                              return (
                                <div
                                  key={item.id}
                                  id={`telefono-row-${item.id}`}
                                  className="border border-slate-100 rounded-2xl p-4 bg-white hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                                >
                                  {/* Info */}
                                  <div className="space-y-1.5 min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">
                                        ID TEL_ {item.id}
                                      </span>
                                      <span className="text-xs font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                        {tipoName}
                                      </span>
                                      {item.note && (
                                        <span className="text-[11px] text-slate-400 italic font-semibold">
                                          ({item.note})
                                        </span>
                                      )}
                                    </div>

                                    <p className="text-sm font-black text-slate-800 tracking-tight select-all">
                                      {item.numero}
                                    </p>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-1.5 flex-shrink-0 w-full md:w-auto border-t md:border-t-0 pt-3 md:pt-0 justify-end">
                                    {(canWrite || isOwnCard) && (
                                      <>
                                        <button
                                          id={`btn-edit-telefono-${item.id}`}
                                          type="button"
                                          onClick={() => handleStartEditTelefono(item)}
                                          className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors border border-slate-100"
                                          title="Modifica"
                                        >
                                          <Edit className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          id={`btn-delete-telefono-${item.id}`}
                                          type="button"
                                          onClick={() => handleDeleteTelefono(item)}
                                          className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors border border-red-100"
                                          title="Elimina"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          {/* ACCORDION EMAILS */}
          <div id="accordion-emails" className="bg-white border rounded-3xl shadow-sm overflow-hidden transition-all h-fit">
          {/* Accordion Trigger Header */}
          <button
            id="btn-accordion-emails-trigger"
            type="button"
            onClick={() => setIsAccordionEmailsOpen(!isAccordionEmailsOpen)}
            className="w-full flex items-center justify-between p-6 hover:bg-slate-50/50 transition-colors text-left focus:outline-none"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight">Emails</h3>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">
                  {emails.length === 0
                    ? 'Nessun indirizzo email associato'
                    : emails.length === 1
                    ? '1 indirizzo email registrato'
                    : `${emails.length} indirizzi email registrati`}
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: isAccordionEmailsOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="p-1 px-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </button>

          <AnimatePresence initial={false}>
            {isAccordionEmailsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="border-t border-slate-100 p-6 bg-slate-50/30">
                  <div className="space-y-6">
                    {isAddingEmail || editingEmailId ? (
                      /* Email Form */
                      <div id="email-form-container" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4">
                        <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                          <Plus className="w-4 h-4 text-indigo-600" />
                          <span>{isAddingEmail ? 'Nuova Email' : 'Modifica Email'}</span>
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                          {/* Tipologia Email select */}
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Tipologia Email</label>
                            <select
                              id="select-email-type"
                              value={emailForm.id_tipo_mail || ''}
                              onChange={e => setEmailForm({ ...emailForm, id_tipo_mail: e.target.value })}
                              className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-indigo-600 appearance-none italic font-bold text-slate-700 min-h-[36px]"
                            >
                              <option value="">Seleziona...</option>
                              {tipologieEmails.map(t => (
                                <option key={t.id} value={t.id}>{t.tipo_email}</option>
                              ))}
                            </select>
                          </div>

                          {/* Email Input */}
                          <div className="col-span-12 md:col-span-6">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Indirizzo Email</label>
                            <input
                              id="input-email-address"
                              type="email"
                              placeholder="esempio@indirizzo.it"
                              value={emailForm.email || ''}
                              onChange={e => setEmailForm({ ...emailForm, email: e.target.value })}
                              className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-indigo-600 min-h-[36px]"
                            />
                          </div>

                          {/* PEC True/False Switch */}
                          <div className="col-span-12 md:col-span-2 flex items-center pt-5">
                            <label className="flex items-center gap-2 cursor-pointer select-none">
                              <input
                                id="checkbox-email-is-pec"
                                type="checkbox"
                                checked={!!emailForm.is_pec}
                                onChange={e => setEmailForm({ ...emailForm, is_pec: e.target.checked })}
                                className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                              />
                              <span className="text-xs font-bold text-slate-600">PEC / Certificata</span>
                            </label>
                          </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
                          <button
                            id="btn-cancel-email"
                            type="button"
                            onClick={() => {
                              setIsAddingEmail(false);
                              setEditingEmailId(null);
                            }}
                            className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
                          >
                            Annulla
                          </button>
                          <button
                            id="btn-save-email"
                            type="button"
                            onClick={handleSaveEmail}
                            disabled={saving}
                            className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:shadow-md transition-colors"
                          >
                            {saving ? 'Salvataggio...' : 'Salva Email'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Email List */
                      <div className="space-y-4">
                        <div id="email-list-header" className="flex justify-between items-center bg-white border border-slate-100/80 rounded-2xl p-4 shadow-xs">
                          <span className="text-xs font-black uppercase text-slate-400 tracking-wider">
                            Elenco Email
                          </span>
                          {(canWrite || isOwnCard) && (
                            <button
                              id="btn-add-email-trigger"
                              type="button"
                              onClick={handleStartAddEmail}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-all flex-shrink-0"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span>Aggiungi Email</span>
                            </button>
                          )}
                        </div>

                        {loadingEmails ? (
                          <div className="p-10 text-center text-slate-400 text-sm animate-pulse">
                            Caricamento email...
                          </div>
                        ) : emails.length === 0 ? (
                          <div className="p-8 text-center bg-white border border-dashed rounded-2xl text-slate-400 text-sm">
                            Nessun indirizzo email inserito per questa persona. Clicca su "Aggiungi Email" per registrarne uno.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {sortedEmails.map((item) => {
                              const tipoName = tipologieEmails.find(t => Number(t.id) === Number(item.id_tipo_mail))?.tipo_email || `Tipo ${item.id_tipo_mail}`;
                              const numericType = Number(item.id_tipo_mail);
                              const showActions = canWrite || (isOwnCard && numericType >= 2 && numericType <= 98);
                              return (
                                <div
                                  key={item.id}
                                  id={`email-row-${item.id}`}
                                  className="border border-slate-100 rounded-2xl p-4 bg-white hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4"
                                >
                                  {/* Info */}
                                  <div className="space-y-1.5 min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">
                                        ID EM_ {item.id}
                                      </span>
                                      <span className="text-xs font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                        {tipoName}
                                      </span>
                                      {item.is_pec && (
                                        <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold border border-amber-200">
                                          PEC
                                        </span>
                                      )}
                                    </div>

                                    <p className="text-sm font-black text-slate-800 tracking-tight select-all">
                                      {item.email}
                                    </p>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-1.5 flex-shrink-0 w-full md:w-auto border-t md:border-t-0 pt-3 md:pt-0 justify-end">
                                    {showActions && (
                                      <>
                                        <button
                                          id={`btn-edit-email-${item.id}`}
                                          type="button"
                                          onClick={() => handleStartEditEmail(item)}
                                          className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors border border-slate-100"
                                          title="Modifica"
                                        >
                                          <Edit className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          id={`btn-delete-email-${item.id}`}
                                          type="button"
                                          onClick={() => handleDeleteEmail(item)}
                                          className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors border border-red-100"
                                          title="Elimina"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          {/* ACCORDION SOCIAL NETWORKS */}
          <div id="accordion-socials" className="bg-white border rounded-3xl shadow-sm overflow-hidden transition-all animate-fade-in h-fit">
          {/* Accordion Trigger Header */}
          <button
            id="btn-accordion-socials-trigger"
            type="button"
            onClick={() => setIsAccordionSocialsOpen(!isAccordionSocialsOpen)}
            className="w-full flex items-center justify-between p-6 hover:bg-slate-50/50 transition-colors text-left focus:outline-none"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-800 tracking-tight">Social Networks</h3>
                <p className="text-xs text-slate-400 font-semibold mt-0.5">
                  {socials.length === 0
                    ? 'Nessun social network associato'
                    : socials.length === 1
                    ? '1 social network registrato'
                    : `${socials.length} social networks registrati`}
                </p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: isAccordionSocialsOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="p-1 px-2 rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors"
            >
              <ChevronDown className="w-5 h-5" />
            </motion.div>
          </button>

          <AnimatePresence initial={false}>
            {isAccordionSocialsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="border-t border-slate-100 p-6 bg-slate-50/30">
                  <div className="space-y-6">
                    {isAddingSocial || editingSocialId ? (
                      /* Social Form */
                      <div id="social-form-container" className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-4 animate-scale-up">
                        <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                          <Plus className="w-4 h-4 text-indigo-600" />
                          <span>{isAddingSocial ? 'Nuovo Social Network' : 'Modifica Social Network'}</span>
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                          {/* Social select with dynamic icon */}
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Social *</label>
                            <div className="relative">
                              <select
                                id="select-social-type"
                                value={socialForm.id_social || ''}
                                onChange={e => setSocialForm({ ...socialForm, id_social: e.target.value })}
                                className="w-full pl-10 pr-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-indigo-600 appearance-none italic font-bold text-slate-700 min-h-[36px]"
                              >
                                <option value="">Seleziona...</option>
                                {tipologieSocial.map(t => (
                                  <option key={t.id} value={t.id}>
                                    {getSocialEmoji(t.piattaforma)} {t.piattaforma}
                                  </option>
                                ))}
                              </select>
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                {socialForm.id_social ? (
                                  getSocialIcon(tipologieSocial.find(t => Number(t.id) === Number(socialForm.id_social))?.piattaforma || '')
                                ) : (
                                  <Globe className="w-4 h-4 text-slate-400" />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Username 1 Input */}
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Username 1 *</label>
                            <input
                              id="input-social-username1"
                              type="text"
                              placeholder="E.g. mariorossi"
                              value={socialForm.username1 || ''}
                              onChange={e => setSocialForm({ ...socialForm, username1: e.target.value })}
                              className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-indigo-600 min-h-[36px]"
                              required
                            />
                          </div>

                          {/* Username 2 Input */}
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Username 2</label>
                            <input
                              id="input-social-username2"
                              type="text"
                              placeholder="Opzionale"
                              value={socialForm.username2 || ''}
                              onChange={e => setSocialForm({ ...socialForm, username2: e.target.value })}
                              className="w-full px-3 py-1.5 bg-slate-50 border rounded-xl text-sm outline-none focus:ring-1 focus:ring-indigo-600 min-h-[36px]"
                            />
                          </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
                          <button
                            id="btn-cancel-social"
                            type="button"
                            onClick={() => {
                              setIsAddingSocial(false);
                              setEditingSocialId(null);
                            }}
                            className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-200 transition-colors"
                          >
                            Annulla
                          </button>
                          <button
                            id="btn-save-social"
                            type="button"
                            onClick={handleSaveSocial}
                            disabled={saving}
                            className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:shadow-md transition-colors"
                          >
                            {saving ? 'Salvataggio...' : 'Salva Social'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Social List */
                      <div className="space-y-4">
                        <div id="social-list-header" className="flex justify-between items-center bg-white border border-slate-100/80 rounded-2xl p-4 shadow-xs">
                          <span className="text-xs font-black uppercase text-slate-400 tracking-wider">
                            Elenco Social Networks
                          </span>
                          {(canWrite || isOwnCard) && (
                            <button
                              id="btn-add-social-trigger"
                              type="button"
                              onClick={handleStartAddSocial}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-xl text-xs font-bold hover:bg-indigo-100 transition-all flex-shrink-0"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              <span>Aggiungi Social</span>
                            </button>
                          )}
                        </div>

                        {loadingSocials ? (
                          <div className="p-10 text-center text-slate-400 text-sm animate-pulse">
                            Caricamento social networks...
                          </div>
                        ) : socials.length === 0 ? (
                          <div className="p-8 text-center bg-white border border-dashed rounded-2xl text-slate-400 text-sm">
                            Nessun account social inserito per questa persona. Clicca su "Aggiungi Social" per registrarne uno.
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {sortedSocials.map((item) => {
                              const tip = tipologieSocial.find(t => Number(t.id) === Number(item.id_social));
                              const platformName = tip?.piattaforma || `Social ID: ${item.id_social}`;
                              const links = getSocialLinks(item);
                              return (
                                <div
                                  key={item.id}
                                  id={`social-row-${item.id}`}
                                  className="border border-slate-100 rounded-2xl p-4 bg-white hover:shadow-md transition-all flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:border-slate-200/80"
                                >
                                  {/* Info */}
                                  <div className="space-y-1.5 min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold flex items-center gap-1.5">
                                        {getSocialIcon(platformName)}
                                        {platformName}
                                      </span>
                                      <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-bold border border-indigo-200">
                                        {item.username1} {item.username2 ? `/ ${item.username2}` : ''}
                                      </span>
                                    </div>

                                    <div className="flex flex-col gap-1.5 pt-1.5">
                                      {links.base && (
                                        <a
                                          href={links.base}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold hover:underline flex items-center gap-1.5 break-all"
                                        >
                                          <Link className="w-3 h-3 shrink-0" />
                                          <span>{links.base}</span>
                                        </a>
                                      )}
                                      {links.alt && (
                                        <a
                                          href={links.alt}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs text-slate-500 hover:text-slate-700 font-semibold hover:underline flex items-center gap-1.5 break-all"
                                        >
                                          <Sparkles className="w-3 h-3 shrink-0" />
                                          <span>Link Alt: {links.alt}</span>
                                        </a>
                                      )}
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-1.5 flex-shrink-0 w-full md:w-auto border-t md:border-t-0 pt-3 md:pt-0 justify-end">
                                    {(canWrite || isOwnCard) && (
                                      <>
                                        <button
                                          id={`btn-edit-social-${item.id}`}
                                          type="button"
                                          onClick={() => handleStartEditSocial(item)}
                                          className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors border border-slate-100 focus:outline-none"
                                          title="Modifica"
                                        >
                                          <Edit className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          id={`btn-delete-social-${item.id}`}
                                          type="button"
                                          onClick={() => handleDeleteSocial(item)}
                                          className="p-1.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors border border-red-100 focus:outline-none"
                                          title="Elimina"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          </div>

          {/* RIGA DI SEPARAZIONE E SEZIONE SCHEDA SOCIO CPGEV (Solo se tipologia_anagrafica === 1) */}
          {data.tipologia_anagrafica === 1 && (
            <>
              {/* RIGA DI SEPARAZIONE NEL BLOCCO SEZIONI */}
              <div id="separatore-scheda-socio" className="col-span-1 lg:col-span-2 my-3 pt-2">
                <div className="relative flex items-center">
                  <div className="flex-grow border-t-2 border-dashed border-amber-200"></div>
                  <div className="flex-shrink mx-4 px-4 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/80 rounded-full flex items-center gap-2.5 text-xs font-black uppercase tracking-wider text-amber-900 shadow-sm">
                    <img 
                      src="/images/gufetto.webp" 
                      alt="Gufo CPGEV" 
                      className="w-4 h-4 object-contain" 
                      referrerPolicy="no-referrer"
                    />
                    <span>Area Riservata Socio CPGEV</span>
                  </div>
                  <div className="flex-grow border-t-2 border-dashed border-amber-200"></div>
                </div>
              </div>

              {/* SEZIONE A TUTTA LARGHEZZA: SCHEDA SOCIO */}
              <div 
                id="sezione-scheda-socio" 
                className="col-span-1 lg:col-span-2 bg-white border border-slate-200/90 rounded-3xl shadow-sm overflow-hidden transition-all"
              >
                {/* Accordion Header */}
                <div 
                  id="btn-accordion-socio-trigger"
                  onClick={() => setIsAccordionSocioOpen(!isAccordionSocioOpen)}
                  className="w-full flex items-center justify-between p-6 hover:bg-slate-50/50 transition-colors text-left cursor-pointer select-none"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200/70 flex items-center justify-center p-2 shadow-sm flex-shrink-0">
                      <img 
                        src="/images/gufetto.webp" 
                        alt="Gufo CPGEV" 
                        className="w-full h-full object-contain drop-shadow-sm" 
                        referrerPolicy="no-referrer"
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h3 className="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                          Scheda Socio
                        </h3>
                        {socioData ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-black bg-emerald-100/70 text-emerald-800 border border-emerald-300/60 shadow-xs">
                            Matricola #{socioData.matricola}
                          </span>
                        ) : (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100/60 text-amber-800 border border-amber-200/70">
                            Non registrata
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 font-semibold mt-0.5">
                        {loadingSocio ? (
                          'Caricamento dati socio da Postgresql...'
                        ) : socioData ? (
                          <>
                            <span className="text-slate-700 font-bold">
                              {getDescrizioneStatoSocio(socioData.stato_socio)}
                            </span>
                            <span className="mx-1.5 text-slate-300">•</span>
                            <span>{getDescrizioneZona(socioData.zona)}</span>
                            <span className="mx-1.5 text-slate-300">•</span>
                            <span>Iscritto dal {new Date(socioData.data_iscrizione).toLocaleDateString('it-IT')}</span>
                          </>
                        ) : (
                          'Nessuna scheda socio associata a questo socio CPGEV'
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {canWrite && !isEditingSocio && (
                      socioData ? (
                        <button
                          id="btn-edit-scheda-socio"
                          type="button"
                          onClick={handleStartEditSocio}
                          className="p-2 px-3.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200/70 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs active:scale-95"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          <span>Modifica Scheda</span>
                        </button>
                      ) : (
                        <button
                          id="btn-crea-scheda-socio"
                          type="button"
                          onClick={handleStartAddSocio}
                          className="p-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm shadow-emerald-200 active:scale-95"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Crea Scheda Socio</span>
                        </button>
                      )
                    )}

                    <motion.div
                      animate={{ rotate: isAccordionSocioOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      className="p-2 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors cursor-pointer"
                      onClick={() => setIsAccordionSocioOpen(!isAccordionSocioOpen)}
                    >
                      <ChevronDown className="w-5 h-5" />
                    </motion.div>
                  </div>
                </div>

                {/* Accordion Body */}
                <AnimatePresence initial={false}>
                  {isAccordionSocioOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-slate-100"
                    >
                      <div className="p-6 sm:p-8 bg-slate-50/40">
                        {loadingSocio ? (
                          <div className="flex items-center justify-center py-12 gap-3 text-slate-400">
                            <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                            <span className="text-sm font-semibold">Caricamento scheda socio...</span>
                          </div>
                        ) : isEditingSocio ? (
                          /* FORM DI MODIFICA / CREAZIONE SCHEDA SOCIO */
                          <div id="form-scheda-socio" className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-6">
                            <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                              <div className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-amber-600" />
                                <h4 className="text-base font-black text-slate-800">
                                  {isAddingSocio ? 'Nuova Registrazione Scheda Socio' : 'Modifica Dati Scheda Socio'}
                                </h4>
                              </div>
                              <span className="text-xs font-semibold text-slate-400">
                                Tabella: <code className="text-slate-600 font-mono">cpgev_soci</code>
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3.5 items-start">
                              {/* Matricola */}
                              <div>
                                <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1.5">
                                  <Hash className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Matricola</span>
                                </label>
                                <input
                                  id="input-socio-matricola"
                                  type="number"
                                  placeholder="Es. 1045"
                                  value={socioForm.matricola}
                                  onChange={(e) => setSocioForm({ ...socioForm, matricola: e.target.value })}
                                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-sm font-bold text-slate-900 focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                  required
                                />
                                <span className="text-[10px] text-slate-400 font-medium mt-1 block">Numero univoco socio</span>
                              </div>

                              {/* Stato Socio - ComboBox che registra tipo e visualizza descrizione da cpgev_stato_socio */}
                              <div id="wrapper-socio-stato">
                                <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1.5">
                                  <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Stato</span>
                                </label>
                                <ComboBox
                                  placeholder="Seleziona Stato..."
                                  options={statiSocioList.map((s) => {
                                    const num = Number(s.tipo);
                                    const formattedTipo = !isNaN(num) && num < 10 ? `0${num}` : `${s.tipo}`;
                                    return {
                                      id: s.tipo,
                                      label: `${formattedTipo} - ${s.descrizione}`
                                    };
                                  })}
                                  value={socioForm.stato_socio}
                                  onChange={(val) => {
                                    setSocioForm((prev: any) => ({
                                      ...prev,
                                      stato_socio: val !== null && val !== undefined && val !== '' ? Number(val) : ''
                                    }));
                                  }}
                                  inputClassName="min-h-[38px] border-slate-300 bg-slate-50 font-bold text-slate-900 py-1.5"
                                />
                                <span className="text-[10px] text-slate-500 font-medium mt-1 block truncate">
                                  Tipo: <strong className="text-slate-800 font-mono">{socioForm.stato_socio ?? '-'}</strong> ({getDescrizioneStatoSocio(socioForm.stato_socio)})
                                </span>
                              </div>

                              {/* Zona di appartenenza - ComboBox */}
                              <div id="wrapper-socio-zona">
                                <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1.5">
                                  <Compass className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Zona</span>
                                </label>
                                <ComboBox
                                  placeholder="Seleziona Zona..."
                                  options={zoneList.map((z) => ({
                                    id: z.id,
                                    label: z.descrizione
                                  }))}
                                  value={socioForm.zona}
                                  onChange={(val) => {
                                    setSocioForm((prev: any) => ({
                                      ...prev,
                                      zona: val !== null && val !== undefined && val !== '' ? Number(val) : ''
                                    }));
                                  }}
                                  inputClassName="min-h-[38px] border-slate-300 bg-slate-50 font-bold text-slate-900 py-1.5"
                                />
                                <span className="text-[10px] text-slate-400 font-medium mt-1 block">Tabella <code className="text-slate-600 font-mono">cpgev_codici_zone</code></span>
                              </div>

                              {/* Data Iscrizione */}
                              <div>
                                <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Data Iscr.</span>
                                </label>
                                <input
                                  id="input-socio-data-iscrizione"
                                  type="date"
                                  value={socioForm.data_iscrizione}
                                  onChange={(e) => setSocioForm({ ...socioForm, data_iscrizione: e.target.value })}
                                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                  required
                                />
                                <span className="text-[10px] text-slate-400 font-medium mt-1 block">Data iscrizione iniziale</span>
                              </div>

                              {/* Data Cessazione */}
                              <div>
                                <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-1.5 flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                  <span>Data Cessazione</span>
                                </label>
                                <input
                                  id="input-socio-data-cessazione"
                                  type="date"
                                  value={socioForm.data_cessazione || ''}
                                  onChange={(e) => setSocioForm({ ...socioForm, data_cessazione: e.target.value })}
                                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                />
                                <span className="text-[10px] text-slate-400 font-medium mt-1 block">Opzionale, solo cessati</span>
                              </div>
                            </div>

                            {/* Sezioni a Tabs (Iscrizione, Decreti e Griglie DB) */}
                            <div className="pt-2">
                              <SezioniSocio
                                isEditing={true}
                                canWrite={canWrite}
                                socioId={socioData?.id}
                                matricola={socioForm.matricola}
                                rawAttributi={socioForm.attributi_aggiuntivi}
                                onChange={(newAttr) => {
                                  setSocioForm((prev: any) => ({
                                    ...prev,
                                    attributi_aggiuntivi: newAttr
                                  }));
                                  setJsonAttributiRaw(JSON.stringify(newAttr, null, 2));
                                }}
                              />
                            </div>

                            {/* Azioni Form */}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100 flex-wrap gap-3">
                              <div>
                                {!isAddingSocio && socioData && (
                                  <button
                                    id="btn-delete-socio-trigger"
                                    type="button"
                                    disabled={savingSocio}
                                    onClick={() => setShowDeleteSocioModal(true)}
                                    className="px-3.5 py-2 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 active:scale-95"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Elimina Scheda</span>
                                  </button>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  id="btn-cancel-socio"
                                  type="button"
                                  disabled={savingSocio}
                                  onClick={handleCancelEditSocio}
                                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all active:scale-95"
                                >
                                  Annulla
                                </button>
                                <button
                                  id="btn-save-socio"
                                  type="button"
                                  disabled={savingSocio}
                                  onClick={handleSaveSocio}
                                  className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-amber-200 flex items-center gap-1.5 active:scale-95 disabled:opacity-50"
                                >
                                  <Save className="w-3.5 h-3.5" />
                                  <span>{savingSocio ? 'Salvataggio...' : 'Salva Scheda Socio'}</span>
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : socioData ? (
                          /* VISUALIZZAZIONE SCHEDA SOCIO ESISTENTE */
                          <div id="dettaglio-scheda-socio" className="space-y-6">
                            {/* Banner Principale */}
                            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-slate-50 border border-amber-200/70 rounded-2xl p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-2xl bg-white border-2 border-amber-300 shadow-sm flex items-center justify-center p-2">
                                  <img 
                                    src="/images/gufetto.webp" 
                                    alt="Gufo CPGEV" 
                                    className="w-full h-full object-contain" 
                                    referrerPolicy="no-referrer"
                                  />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-2xl font-black text-slate-900 tracking-tight">
                                      Tessera #{socioData.matricola}
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-black bg-amber-600 text-white shadow-xs">
                                      {getDescrizioneStatoSocio(socioData.stato_socio)}
                                    </span>
                                    {socioData.data_cessazione && (
                                      <span className="px-3 py-1 rounded-full text-xs font-black bg-red-100 text-red-800 border border-red-200">
                                        Cessato
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs font-bold text-slate-500 mt-1">
                                    {getDescrizioneZona(socioData.zona)}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-4 text-xs font-semibold text-slate-600 bg-white/80 backdrop-blur-xs p-3 rounded-xl border border-amber-200/50">
                                <div>
                                  <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-bold">Iscrizione</span>
                                  <span className="text-slate-900 font-black">
                                    {new Date(socioData.data_iscrizione).toLocaleDateString('it-IT')}
                                  </span>
                                </div>
                                {socioData.data_cessazione && (
                                  <>
                                    <div className="h-6 w-px bg-slate-200"></div>
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase tracking-wider font-bold">Cessazione</span>
                                      <span className="text-red-600 font-black">
                                        {new Date(socioData.data_cessazione).toLocaleDateString('it-IT')}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Dati Dettagliati in Griglia (su una riga se schermo lo consente) */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3.5">
                              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1 flex items-center gap-1.5">
                                  <Hash className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Matricola</span>
                                </span>
                                <span className="text-lg font-black text-slate-800">
                                  {socioData.matricola}
                                </span>
                              </div>

                              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1 flex items-center gap-1.5">
                                  <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Stato</span>
                                </span>
                                <span className="text-sm font-bold text-slate-800 truncate block" title={getDescrizioneStatoSocio(socioData.stato_socio)}>
                                  {getDescrizioneStatoSocio(socioData.stato_socio)}
                                </span>
                              </div>

                              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1 flex items-center gap-1.5">
                                  <Compass className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Zona</span>
                                </span>
                                <span className="text-sm font-bold text-slate-800 truncate block" title={getDescrizioneZona(socioData.zona)}>
                                  {getDescrizioneZona(socioData.zona)}
                                </span>
                              </div>

                              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1 flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5 text-amber-600" />
                                  <span>Data Iscr.</span>
                                </span>
                                <span className="text-sm font-bold text-slate-800">
                                  {socioData.data_iscrizione ? new Date(socioData.data_iscrizione).toLocaleDateString('it-IT') : '-'}
                                </span>
                              </div>

                              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs">
                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400 block mb-1 flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                  <span>Data Cessazione</span>
                                </span>
                                <span className={cn("text-sm font-bold", socioData.data_cessazione ? "text-red-600" : "text-slate-400")}>
                                  {socioData.data_cessazione ? new Date(socioData.data_cessazione).toLocaleDateString('it-IT') : '-'}
                                </span>
                              </div>
                            </div>

                            {/* Sezioni a Tabs (Iscrizione, Decreti e Griglie DB) */}
                            <div className="pt-2">
                              <SezioniSocio
                                isEditing={false}
                                canWrite={canWrite}
                                socioId={socioData?.id}
                                matricola={socioData?.matricola}
                                rawAttributi={socioData?.attributi_aggiuntivi}
                              />
                            </div>

                            {/* Footer con Timestamp di Audit */}
                            <div className="flex items-center justify-between text-[11px] text-slate-400 font-semibold px-1 pt-1 flex-wrap gap-2">
                              <span>
                                Inserimento: {socioData.data_inserimento ? new Date(socioData.data_inserimento).toLocaleString('it-IT') : '-'}
                              </span>
                              <span>
                                Ultimo aggiornamento: {socioData.data_aggiornamento ? new Date(socioData.data_aggiornamento).toLocaleString('it-IT') : '-'}
                              </span>
                            </div>
                          </div>
                        ) : (
                          /* STATO VUOTO: NESSUNA SCHEDA SOCIO ANCORA CREATA */
                          <div id="empty-state-socio" className="text-center py-10 px-4 bg-white border border-dashed border-amber-300/80 rounded-2xl">
                            <div className="w-16 h-16 rounded-3xl bg-amber-50 border border-amber-200 mx-auto mb-4 flex items-center justify-center p-3 shadow-xs">
                              <img 
                                src="/images/gufetto.webp" 
                                alt="Gufo CPGEV" 
                                className="w-full h-full object-contain" 
                                referrerPolicy="no-referrer"
                              />
                            </div>
                            <h4 className="text-base font-black text-slate-800">
                              Nessuna Scheda Socio Registrata
                            </h4>
                            <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 mb-5">
                              Questa persona è classificata come <strong className="text-amber-800">Socio CPGEV</strong>, ma non è ancora stata creata la relativa scheda socio nella tabella dedicata.
                            </p>
                            {canWrite && (
                              <button
                                id="btn-crea-scheda-socio-empty"
                                type="button"
                                onClick={handleStartAddSocio}
                                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-amber-200 inline-flex items-center gap-2 active:scale-95"
                              >
                                <Plus className="w-4 h-4" />
                                <span>Registra Scheda Socio</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      )}

      <AnimatePresence>
        {showDeleteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-red-500 p-8 text-white text-center">
                <Trash2 className="w-14 h-14 mx-auto mb-4 animate-bounce" />
                <h2 className="text-3xl font-black uppercase tracking-tighter italic leading-none">ATTENZIONE!!</h2>
                <p className="font-bold text-lg mt-2 opacity-90">Azione irreversibile!</p>
              </div>
              
              <div className="p-10 space-y-8 text-center">
                <p className="text-slate-600">
                  <span className="font-black text-slate-900 uppercase block text-2xl mb-1 tracking-tight">
                    {data.cognome} {data.nome}
                  </span>
                  verrà rimossa definitivamente dall' <span className="font-bold text-red-600">ANAGRAFICA</span>
                  <br />
                  <span className="font-black text-red-600 uppercase tracking-widest text-[10px] border-b-2 border-red-600 pb-0.5 inline-block mt-4">
                    INSIEME A TUTTI I DATI CORRELATI!!!!
                  </span>
                </p>

                <div className="space-y-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block">
                    Digita: <span className="text-primary italic">ho capito</span> qui sotto
                  </label>
                  <input 
                    type="text"
                    autoFocus
                    placeholder="Digita qui..."
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value.toLowerCase())}
                    className="w-full px-6 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-center font-black text-primary focus:border-primary focus:ring-4 focus:ring-primary/5 outline-none transition-all placeholder:font-normal placeholder:italic"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <button 
                    onClick={() => setShowDeleteModal(false)}
                    className="px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Annulla
                  </button>
                  <button 
                    disabled={confirmText !== 'ho capito' || saving}
                    onClick={handleDelete}
                    className={`px-6 py-4 rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all ${
                      confirmText === 'ho capito' 
                      ? 'bg-red-600 text-white shadow-xl shadow-red-200 hover:scale-[1.02] active:scale-[0.98]' 
                      : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                    }`}
                  >
                    {saving ? '...' : 'Prosegui'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {emailToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setEmailToDelete(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-red-500 p-8 text-white text-center">
                <Trash2 className="w-14 h-14 mx-auto mb-4 animate-bounce" />
                <h2 className="text-2xl font-black uppercase tracking-tighter italic leading-none">CONFERMA ELIMINAZIONE</h2>
                <p className="font-bold text-sm mt-2 opacity-90">Eliminazione Indirizzo Email</p>
              </div>
              
              <div className="p-10 space-y-8 text-center">
                <div className="text-slate-600">
                  Sei sicuro di voler eliminare definitivamente questo indirizzo email dall&apos;anagrafica?
                  <span className="font-black text-slate-900 block text-xl mt-4 select-all">
                    {emailToDelete.email}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <button 
                    onClick={() => setEmailToDelete(null)}
                    className="px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Annulla
                  </button>
                  <button 
                    disabled={saving}
                    onClick={handleConfirmDeleteEmail}
                    className="px-6 py-4 bg-red-600 text-white shadow-xl shadow-red-200 hover:scale-[1.02] active:scale-[0.98] rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all"
                  >
                    {saving ? '...' : 'Elimina'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {addressToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setAddressToDelete(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-red-500 p-8 text-white text-center">
                <Trash2 className="w-14 h-14 mx-auto mb-4 animate-bounce" />
                <h2 className="text-2xl font-black uppercase tracking-tighter italic leading-none">CONFERMA ELIMINAZIONE</h2>
                <p className="font-bold text-sm mt-2 opacity-90">Eliminazione Indirizzo Fisico</p>
              </div>
              
              <div className="p-10 space-y-8 text-center">
                <div className="text-slate-600">
                  Sei sicuro di voler eliminare definitivamente questo indirizzo fisico dall&apos;anagrafica?
                  <span className="font-black text-slate-900 block text-lg mt-4 leading-snug">
                    {addressToDelete.via || ''} {addressToDelete.numero_civico || ''}
                    {addressToDelete.comune_desc ? `, ${addressToDelete.comune_desc}` : ''}
                    {addressToDelete.cap ? ` (${addressToDelete.cap})` : ''}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <button 
                    onClick={() => setAddressToDelete(null)}
                    className="px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Annulla
                  </button>
                  <button 
                    disabled={saving}
                    onClick={handleConfirmDeleteAddress}
                    className="px-6 py-4 bg-red-600 text-white shadow-xl shadow-red-200 hover:scale-[1.02] active:scale-[0.98] rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all"
                  >
                    {saving ? '...' : 'Elimina'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {socialToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSocialToDelete(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-red-500 p-8 text-white text-center">
                <Trash2 className="w-14 h-14 mx-auto mb-4 animate-bounce" />
                <h2 className="text-2xl font-black uppercase tracking-tighter italic leading-none">CONFERMA ELIMINAZIONE</h2>
                <p className="font-bold text-sm mt-2 opacity-90">Eliminazione Social Network</p>
              </div>
              
              <div className="p-10 space-y-8 text-center">
                <div className="text-slate-600">
                  Sei sicuro di voler eliminare definitivamente questo account social dall&apos;anagrafica?
                  <span className="font-black text-slate-900 block text-xl mt-4 select-all">
                    @{socialToDelete.username1}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <button 
                    onClick={() => setSocialToDelete(null)}
                    className="px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Annulla
                  </button>
                  <button 
                    disabled={saving}
                    onClick={handleConfirmDeleteSocial}
                    className="px-6 py-4 bg-red-600 text-white shadow-xl shadow-red-200 hover:scale-[1.02] active:scale-[0.98] rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all"
                  >
                    {saving ? '...' : 'Elimina'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {telefonoToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setTelefonoToDelete(null)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-red-500 p-8 text-white text-center">
                <Trash2 className="w-14 h-14 mx-auto mb-4 animate-bounce" />
                <h2 className="text-2xl font-black uppercase tracking-tighter italic leading-none">CONFERMA ELIMINAZIONE</h2>
                <p className="font-bold text-sm mt-2 opacity-90">Eliminazione Recapito Telefonico</p>
              </div>
              
              <div className="p-10 space-y-8 text-center">
                <div className="text-slate-600">
                  Sei sicuro di voler eliminare definitivamente questo numero di telefono dall&apos;anagrafica?
                  <span className="font-black text-slate-900 block text-xl mt-4 select-all">
                    {telefonoToDelete.numero}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <button 
                    onClick={() => setTelefonoToDelete(null)}
                    className="px-6 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Annulla
                  </button>
                  <button 
                    disabled={saving}
                    onClick={handleConfirmDeleteTelefono}
                    className="px-6 py-4 bg-red-600 text-white shadow-xl shadow-red-200 hover:scale-[1.02] active:scale-[0.98] rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all"
                  >
                    {saving ? '...' : 'Elimina'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* MODALE DI CONFERMA ELIMINAZIONE SCHEDA SOCIO */}
        {showDeleteSocioModal && socioData && (
          <div id="modal-delete-socio" className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteSocioModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-red-500 p-8 text-white text-center">
                <Trash2 className="w-14 h-14 mx-auto mb-4 animate-bounce" />
                <h2 className="text-2xl font-black uppercase tracking-tighter italic leading-none">CONFERMA ELIMINAZIONE</h2>
                <p className="font-bold text-sm mt-2 opacity-90">Eliminazione Scheda Socio CPGEV</p>
              </div>
              
              <div className="p-8 space-y-6 text-center">
                <div className="text-slate-600 text-sm">
                  Sei sicuro di voler eliminare la Scheda Socio per <strong>{data.nome} {data.cognome}</strong>?
                  <span className="font-black text-slate-900 block text-xl mt-3 select-all bg-slate-50 py-2 rounded-xl border border-slate-200">
                    Matricola #{socioData.matricola}
                  </span>
                  <span className="text-xs font-bold text-slate-500 block mt-1">
                    Stato: {getDescrizioneStatoSocio(socioData.stato_socio)}
                  </span>
                  <p className="text-xs text-red-500 mt-2 font-semibold">
                    I dati anagrafici generali non verranno rimossi.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <button 
                    id="btn-cancel-delete-socio"
                    type="button"
                    disabled={savingSocio}
                    onClick={() => setShowDeleteSocioModal(false)}
                    className="px-6 py-3.5 bg-slate-100 text-slate-600 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
                  >
                    Annulla
                  </button>
                  <button 
                    id="btn-confirm-delete-socio"
                    type="button"
                    disabled={savingSocio}
                    onClick={handleConfirmDeleteSocio}
                    className="px-6 py-3.5 bg-red-600 text-white shadow-xl shadow-red-200 hover:scale-[1.02] active:scale-[0.98] rounded-2xl font-black uppercase text-xs tracking-[0.2em] transition-all disabled:opacity-50"
                  >
                    {savingSocio ? '...' : 'Elimina'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {otpModalOpen && (
          <div id="otp-verification-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOtpModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-indigo-600 p-8 text-white text-center">
                <ShieldCheck className="w-14 h-14 mx-auto mb-4 animate-pulse" />
                <h2 className="text-2xl font-black uppercase tracking-tight text-white">Verifica Email</h2>
                <p className="font-bold text-sm mt-1 opacity-90 text-white/95">Conferma la sicurezza del tuo indirizzo</p>
              </div>
              
              <div className="p-8 space-y-6 text-center">
                <p className="text-slate-600 text-sm">
                  Abbiamo inviato un codice OTP di 5 cifre all'indirizzo email:
                  <span className="font-black text-slate-800 block text-base mt-2 select-all bg-indigo-50/50 p-2 rounded-xl border border-indigo-100/50">{otpEmailTarget}</span>
                  Il codice scadrà fra <span className="font-bold text-indigo-600">10 minuti</span>.
                </p>

                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                    Inserisci Codice OTP (5 cifre)
                  </label>
                  <input 
                    id="otp-digits-input"
                    type="text"
                    maxLength={5}
                    placeholder="e.g. 12345"
                    value={otpCodeInput}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setOtpCodeInput(val);
                      if (val.length === 5) {
                        setOtpError('');
                      }
                    }}
                    className="w-48 px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl text-center font-black tracking-[0.25em] text-xl text-slate-800 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none transition-all placeholder:font-normal placeholder:tracking-normal placeholder:text-sm placeholder:italic mx-auto block"
                  />
                  {otpError && (
                    <p className="text-red-500 text-xs font-black mt-1 max-w-xs mx-auto">
                      {otpError}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2">
                  <button 
                    id="btn-otp-cancel"
                    onClick={() => setOtpModalOpen(false)}
                    className="px-5 py-3 bg-slate-100 text-slate-600 rounded-xl font-bold text-xs uppercase hover:bg-slate-200 transition-all"
                  >
                    Annulla
                  </button>
                  <button 
                    id="btn-otp-confirm"
                    disabled={otpCodeInput.length !== 5 || verifyingOtp}
                    onClick={handleVerifyOtpAndSave}
                    className="px-5 py-3 bg-indigo-600 text-white rounded-xl font-bold text-xs uppercase shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {verifyingOtp ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        <span>Verifica...</span>
                      </>
                    ) : (
                      <span>Verifica</span>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* MODAL CREAZIONE NUOVA PERSONA VIRTUALE */}
        {isCreaNuovaPersonaOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsCreaNuovaPersonaOpen(false);
                setCurMemberIndexForNew(null);
              }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[32px] shadow-2xl overflow-hidden"
            >
              <div className="bg-indigo-600 p-6 text-white text-center">
                <Sparkles className="w-12 h-12 mx-auto mb-3 animate-pulse" />
                <h2 className="text-2xl font-black uppercase tracking-tight">Registrazione Virtuale</h2>
                <p className="font-bold text-xs opacity-90 mt-1 font-sans">Registra al volo un nuovo componente emerso dall'analisi AI</p>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Cognome *</label>
                    <input 
                      type="text"
                      value={nuovaPersonaForm.cognome}
                      onChange={e => setNuovaPersonaForm({...nuovaPersonaForm, cognome: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all text-slate-800"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Nome *</label>
                    <input 
                      type="text"
                      value={nuovaPersonaForm.nome}
                      onChange={e => setNuovaPersonaForm({...nuovaPersonaForm, nome: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all text-slate-800"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Codice Fiscale</label>
                    <input 
                      type="text"
                      maxLength={16}
                      value={nuovaPersonaForm.codice_fiscale}
                      onChange={e => setNuovaPersonaForm({...nuovaPersonaForm, codice_fiscale: e.target.value.toUpperCase()})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm font-mono focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all text-slate-850"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Sesso</label>
                    <select
                      value={nuovaPersonaForm.sesso}
                      onChange={e => setNuovaPersonaForm({...nuovaPersonaForm, sesso: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all text-slate-700"
                    >
                      <option value="M">Maschio</option>
                      <option value="F">Femmina</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Data Nascita (GG/MM/AAAA o AAAA-MM-GG)</label>
                    <input 
                      type="text"
                      placeholder="e.g. 15/08/1980"
                      value={nuovaPersonaForm.data_nascita}
                      onChange={e => setNuovaPersonaForm({...nuovaPersonaForm, data_nascita: e.target.value})}
                      className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all text-slate-800"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Comune Nascita</label>
                    <input 
                      type="text"
                      value={nuovaPersonaForm.comune_nascita}
                      disabled
                      className="w-full px-4 py-2 border bg-slate-50 text-slate-400 rounded-xl text-xs font-semibold select-none"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider block mb-1">Provincia Nascita</label>
                    <input 
                      type="text"
                      value={nuovaPersonaForm.provincia_nascita}
                      disabled
                      className="w-full px-4 py-2 border bg-slate-50 text-slate-400 rounded-xl text-xs font-semibold select-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4">
                  <button 
                    type="button"
                    onClick={() => {
                      setIsCreaNuovaPersonaOpen(false);
                      setCurMemberIndexForNew(null);
                    }}
                    className="px-5 py-3 bg-slate-100 text-slate-600 rounded-2xl font-black text-xs uppercase hover:bg-slate-200 transition-all font-sans cursor-pointer"
                  >
                    Annulla
                  </button>
                  <button 
                    type="button"
                    disabled={isSavingVirtualPersona}
                    onClick={handleSaveVirtualPersona}
                    className="px-5 py-3 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-sans cursor-pointer"
                  >
                    {isSavingVirtualPersona ? (
                      <>
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        <span>Registrazione...</span>
                      </>
                    ) : (
                      <span>Registra</span>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DettaglioPersona;
